import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Checkbox,
    Col,
    Descriptions,
    Divider,
    Drawer,
    Form,
    Input,
    InputNumber,
    Modal,
    Row,
    Select,
    Space,
    Statistic,
    Table,
    Tabs,
    Tag,
    Typography
} from 'antd';
import {
    ClearOutlined,
    CreditCardOutlined,
    FileSearchOutlined,
    PlusOutlined,
    ReloadOutlined,
    SaveOutlined,
    SearchOutlined,
    StopOutlined,
    SwapOutlined,
    EyeOutlined
} from '@ant-design/icons';
import { connect } from 'react-redux';
import { AppState } from '../store';
import {
    getClientData,
    getContractSalesDocuments,
    getContractsExpiring,
    getSalesDocuments,
    getPlans,
    getOrganizationPaymentMethod,
    getOrganizationLegalDocuments,
    getLegalDocuments,
    postContractSalesDocument,
    postContract,
    getOrganizationClientData,
    preparePublicPayPhoneTrial,
    getOrganizationCardTokens,
    getActiveOrganizationCardToken,
    prepareOrganizationCardTokenPayPhone,
    chargeContractWithCardToken,
    disableOrganizationCardToken,
    cancelContract,
    cancelSalesDocument,
    getExpenseCategories,
    postExpenseCategory,
    putExpenseCategory,
    getExpenses,
    postExpense,
    putExpense,
    cancelExpense,
    getExpenseDocument,
    getFinancialMetrics,
    getExpenseOptions,
    getCompanyUsers,
    postCompanyUser,
    putCompanyUser,
    getPendingPaymentRequestInvoices,
    getOrganizationPaymentRequests,
    getPendingCardValidationRequests,
    regeneratePaymentRequestPayPhone,
    postPaymentRequestSalesDocument,
    reversePaymentRequestPayPhone,
    searchOrganizations,
    getActiveSpeciality,
    getMedicalRecordSections,
    postOnboardingRegister,
    getProfile,
    getOrganizationManagementCenters
} from '../store/user/thunks';
import {
    ClientData,
    ContractExpiring,
    ContractSalesDocumentRequest,
    OrganizationPaymentMethod,
    OrganizationSearch,
    OrganizationLegalDocument,
    OrganizationSalesDocument,
    LegalDocument,
    MedicalRecordSection,
    Plan,
    SalesDocumentFileRequest,
    SalesDocumentPaymentMethodRequest,
    Speciality
} from '../store/user/types';
import PlanBillingMetrics from './PlanBillingMetrics';
import PlanBillingExpenses from './PlanBillingExpenses';
import PlanBillingPeople from './PlanBillingPeople';
import PlanBillingPendingInvoices from './PlanBillingPendingInvoices';
import PlanBillingPendingCardValidation from './PlanBillingPendingCardValidation';
import BillingConfiguration from './BillingConfiguration';
import DirectSalesDocumentManagement from './DirectSalesDocumentManagement';
import SalesDocumentsManagement from './SalesDocumentsManagement';
import OrganizationContractManagement from './OrganizationContractManagement';
import { formatCalendarDate, formatUtcDate } from '../services/dateTime';

const { Title, Text } = Typography;

interface PaymentRow {
    key: string;
    organizationPaymentMethodId: string;
    amount: number;
}

interface PayPhonePrepareResponse {
    clientTransactionId: string;
    paymentId: string;
    payWithPayPhone?: string;
    payWithCard?: string;
    bridgeToken?: string;
}

interface OnboardingRegisterResponse {
    success: boolean;
    organizationId: string;
    userId: string;
    requiresCardValidation: boolean;
    payPhone?: PayPhonePrepareResponse | null;
}

interface PaymentRequest {
    id: string;
    organizationId: string;
    organizationName: string;
    currentContractId?: string | null;
    currentContractNumber?: number | null;
    generatedContractId?: string | null;
    generatedContractNumber?: number | null;
    salesDocumentId?: string | null;
    planName: string;
    provider: string;
    clientTransactionId?: string | null;
    providerTransactionId?: string | null;
    authorizationCode?: string | null;
    status: string;
    invoiceStatus: string;
    billingPeriods: number;
    currency: string;
    total: number;
    paidAt?: string | null;
    createdAt?: string | null;
    updatedAt?: string | null;
}

interface OrganizationCardToken {
    id?: string | null;
    organizationId?: string | null;
    provider?: string | null;
    maskedCard?: string | null;
    cardBrand?: string | null;
    cardExpiryMonth?: number | null;
    cardExpiryYear?: number | null;
    cardHolderName?: string | null;
    document?: string | null;
    email?: string | null;
    phone?: string | null;
    status?: string | null;
    validationTransactionId?: string | null;
    validationAuthorizationCode?: string | null;
    validationAmount?: number | null;
    validationCurrency?: string | null;
    validationPaidAt?: string | null;
    reverseStatus?: string | null;
    reverseTransactionId?: string | null;
    reverseAt?: string | null;
    validationClientTransactionId?: string | null;
}

interface OrganizationBillingRow {
    organizationId: string;
    organizationName: string;
    organizationPhone?: string | null;
    phone?: string | null;
    billingEmail: string;
    contracts: ContractExpiring[];
    nextContract: ContractExpiring;
    hasContract: boolean;
}

const pendingPayPhoneStorageKey = 'tbc.payphone.pendingContractPayment';
const defaultWeekDayIds = [
    '2ab3d3ed-75d9-44dc-9de5-5797f2234be5',
    '5228a37d-4035-4443-9f8e-dcaa813f86bf',
    '9946d4f1-711a-48b7-9f36-99a596767d75',
    '5fb31e63-88aa-443b-b2e4-aeaae93a6661',
    '86d01964-739b-4673-9741-e1cef48834b5'
];
const defaultWeekDayLabels = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];
const doctorRolSystemId = '1516006a-9645-4d1c-9df6-6f1bad9a5d2c';
const nurseRolSystemId = '74afcb09-660b-4c02-bc9c-18e0f9147474';
const defaultSequences = [
    { name: 'MEDICAMENTOS', sequence: 1 },
    { name: 'LABORATORIO', sequence: 1 },
    { name: 'PROCEDIMIENTO', sequence: 1 },
    { name: 'IMAGEN', sequence: 1 }
];

const isClinicPlan = (plan?: Plan | null) => {
    const clinicService = (plan?.services || []).find(service =>
        String(service.resourceType || '').toUpperCase() === 'ESTABLECIMENTO'
        && String(service.name || '').trim().toUpperCase().includes('CLINICA'));

    return !!clinicService;
};

const getPlanEstablishmentServiceId = (plan?: Plan | null) =>
    (plan?.services || []).find(service =>
        String(service.resourceType || '').toUpperCase() === 'ESTABLECIMENTO'
        && String(service.name || '').trim().toUpperCase().includes('CLINICA'))?.id;

type ContractStatusFilter = 'TODOS' | 'ACTIVO' | 'MORA' | 'CANCELADO';
type SalesDocumentStatusFilter = 'PAGADO' | 'ANULADO' | 'TODOS';

const emptyClientData: ClientData = {
    ruc: '',
    name: '',
    email: '',
    phone: '',
    address: ''
};

const money = (value: number, currency = 'USD') =>
    new Intl.NumberFormat('es-EC', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2
    }).format(value || 0);

const getStatusColor = (status?: string | null) => {
    if (status === 'PAGADO' || status === 'ACTIVO') {
        return 'green';
    }

    if (status === 'PENDIENTE' || status === 'BORRADOR' || status === 'MORA') {
        return 'gold';
    }

    if (status === 'ANULADO' || status === 'VENCIDO' || status === 'CANCELADO') {
        return 'red';
    }

    return 'default';
};

const filterContractsByStatus = (contracts: ContractExpiring[], status: ContractStatusFilter) => {
    if (status === 'TODOS') {
        return contracts;
    }

    return contracts.filter(contract => contract.status === status);
};

const isPaidContract = (contract: ContractExpiring) => Number(contract.totalPrice || 0) > 0;

const isTrialContract = (contract: ContractExpiring) => {
    return String(contract.subscriptionStatus || '').toUpperCase() === 'TRIAL'
        || (!!contract.trialEndAt && !contract.hasSalesDocument && !contract.lastSalesDocumentId);
};

const hasPlanTrial = (plan?: Plan | null) => {
    return Number(plan?.trialDays || 0) > 0;
};

const getPlanPayPhoneInitialAmount = (plan?: Plan | null) => {
    if (!plan) {
        return 0;
    }

    return hasPlanTrial(plan)
        ? Number(plan.trialValidationAmount || 1)
        : Number(plan.price || 0);
};

const sortContractsDescending = (contracts: ContractExpiring[]) => [...contracts].sort((a, b) => {
    const contractNumberCompare = Number(b.contractNumber || 0) - Number(a.contractNumber || 0);
    if (contractNumberCompare !== 0) {
        return contractNumberCompare;
    }

    const startCompare = new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
    if (startCompare !== 0) {
        return startCompare;
    }

    return new Date(b.endDate).getTime() - new Date(a.endDate).getTime();
});

const getCurrentOrganizationContract = (contracts: ContractExpiring[]) => {
    const activeContracts = contracts.filter(contract => contract.status === 'ACTIVO');

    if (activeContracts.length > 0) {
        const trialContracts = activeContracts.filter(contract => isTrialContract(contract));
        const candidates = trialContracts.length > 0 ? trialContracts : activeContracts;

        return candidates.sort((a, b) => {
            const billingA = a.nextBillingAt ? new Date(a.nextBillingAt).getTime() : Number.MAX_SAFE_INTEGER;
            const billingB = b.nextBillingAt ? new Date(b.nextBillingAt).getTime() : Number.MAX_SAFE_INTEGER;
            if (billingA !== billingB) {
                return billingA - billingB;
            }

            return new Date(a.endDate).getTime() - new Date(b.endDate).getTime();
        })[0];
    }

    return [...contracts].sort((a, b) => {
        const endCompare = new Date(b.endDate).getTime() - new Date(a.endDate).getTime();
        if (endCompare !== 0) {
            return endCompare;
        }

        return Number(b.contractNumber || 0) - Number(a.contractNumber || 0);
    })[0];
};

const createPendingOrganizationContract = (organization: OrganizationSearch): ContractExpiring => ({
    contractId: '',
    organizationId: organization.id,
    organizationName: organization.name,
    organizationPhone: organization.phone,
    phone: organization.phone,
    planId: '',
    planName: 'Sin contrato',
    contractNumber: 0,
    startDate: '',
    endDate: '',
    status: 'PENDIENTE',
    billingCycle: '',
    discountPercentage: 0,
    totalPrice: 0,
    currency: 'USD',
    billingEmail: organization.email,
    subscriptionStatus: null,
    trialStartAt: null,
    trialEndAt: null,
    nextBillingAt: null,
    cardTokenId: null,
    daysToExpire: 0,
    hasSalesDocument: false,
    hasPendingSalesDocument: false,
    hasPaidSalesDocument: false,
    lastSalesDocumentId: null,
    lastSalesDocumentNumber: null,
    lastSalesDocumentStatus: null
});

const hasRegisteredInvoice = (contract: ContractExpiring) => {
    return !!contract.lastSalesDocumentId
        || !!contract.lastSalesDocumentStatus
        || contract.hasSalesDocument
        || contract.hasPaidSalesDocument
        || contract.hasPendingSalesDocument;
};

const renderCardTokenStatus = (status?: string | null) => {
    const normalized = (status || '').toUpperCase();

    if (normalized === 'ACTIVO') {
        return <Tag color="green">ACTIVO</Tag>;
    }

    if (normalized === 'PENDIENTE') {
        return <Tag color="orange">PENDIENTE</Tag>;
    }

    if (normalized === 'ERROR') {
        return <Tag color="red">ERROR</Tag>;
    }

    if (normalized === 'INACTIVO') {
        return <Tag>INACTIVO</Tag>;
    }

    return <Tag color="default">NO REGISTRADO</Tag>;
};

const renderReverseStatus = (status?: string | null) => {
    const normalized = (status || '').toUpperCase();

    if (normalized === 'REVERSADO') {
        return <Tag color="green">REVERSADO</Tag>;
    }

    if (normalized === 'PENDIENTE') {
        return <Tag color="orange">PENDIENTE</Tag>;
    }

    if (normalized === 'ERROR') {
        return <Tag color="red">ERROR</Tag>;
    }

    if (normalized === 'NO_APLICA') {
        return <Tag>NO APLICA</Tag>;
    }

    return 'No aplica';
};

const formatCardTokenLabel = (token?: OrganizationCardToken | null) => {
    if (!token?.maskedCard) {
        return 'No registrada';
    }

    return `${token.cardBrand || 'Tarjeta'} ${token.maskedCard}`;
};

const formatCardExpiry = (token?: OrganizationCardToken | null) => {
    if (!token?.cardExpiryMonth || !token?.cardExpiryYear) {
        return 'No registrada';
    }

    return `${String(token.cardExpiryMonth).padStart(2, '0')}/${token.cardExpiryYear}`;
};

const fileToBase64 = (file: File): Promise<SalesDocumentFileRequest> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
        const result = String(reader.result || '');
        const base64File = result.includes(',') ? result.split(',')[1] : result;
        resolve({
            base64File,
            fileName: file.name
        });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
});

const PlanBilling: React.FC<any> = (props: any) => {
    const moduleView = props.moduleView || 'contracts';
    const moduleTitle = moduleView === 'billing'
        ? 'Gestión de facturación'
        : moduleView === 'company'
            ? 'Gestión de empresa'
            : 'Gestión de contratos';
    const moduleDescription = moduleView === 'billing'
        ? 'Administra la configuración fiscal y los comprobantes de TheBlueCoding.'
        : moduleView === 'company'
            ? 'Administra las personas, gastos y métricas internas de TheBlueCoding.'
            : 'Administra contratos, planes contratados y su vigencia por organización.';
    const [form] = Form.useForm();
    const [registerForm] = Form.useForm();
    const [contracts, setContracts] = useState<ContractExpiring[]>([]);
    const [salesDocuments, setSalesDocuments] = useState<OrganizationSalesDocument[]>([]);
    const [paymentMethods, setPaymentMethods] = useState<OrganizationPaymentMethod[]>([]);
    const [plans, setPlans] = useState<Plan[]>([]);
    const [specialities, setSpecialities] = useState<Speciality[]>([]);
    const [medicalRecordSections, setMedicalRecordSections] = useState<MedicalRecordSection[]>([]);
    const [onboardingLegalDocuments, setOnboardingLegalDocuments] = useState<LegalDocument[]>([]);
    const [selectedContract, setSelectedContract] = useState<ContractExpiring | null>(null);
    const [selectedSalesDocument, setSelectedSalesDocument] = useState<OrganizationSalesDocument | null>(null);
    const [documentPreview, setDocumentPreview] = useState<{ title: string; url?: string; contentText?: string; contentType?: 'TEXT' | 'HTML' } | null>(null);
    const [legalDocuments] = useState<OrganizationLegalDocument[]>([]);
    const [currentLegalDocuments] = useState<LegalDocument[]>([]);
    const [legalDocumentsOrganization, setLegalDocumentsOrganization] = useState<ContractExpiring | null>(null);
    const [selectedOrganizationContracts, setSelectedOrganizationContracts] = useState<OrganizationBillingRow | null>(null);
    const [selectedOrganizationManagement, setSelectedOrganizationManagement] = useState<OrganizationBillingRow | null>(null);
    const [managementActiveTab, setManagementActiveTab] = useState<string>('summary');
    const [managementLegalDocuments, setManagementLegalDocuments] = useState<OrganizationLegalDocument[]>([]);
    const [managementCurrentLegalDocuments, setManagementCurrentLegalDocuments] = useState<LegalDocument[]>([]);
    const [managementPaymentRequests, setManagementPaymentRequests] = useState<PaymentRequest[]>([]);
    const [paymentRows, setPaymentRows] = useState<PaymentRow[]>([]);
    const [isBillingModalOpen, setIsBillingModalOpen] = useState<boolean>(false);
    const [days, setDays] = useState<number | null>(null);
    const [organizationId, setOrganizationId] = useState<string>('');
    const [contractStatus, setContractStatus] = useState<ContractStatusFilter>('TODOS');
    const [salesDocumentStatusFilter, setSalesDocumentStatusFilter] = useState<SalesDocumentStatusFilter>('PAGADO');
    const [organizationOptions, setOrganizationOptions] = useState<OrganizationSearch[]>([]);
    const [billingPeriods, setBillingPeriods] = useState<number>(1);
    const [selectedPlanId, setSelectedPlanId] = useState<string>('');
    const [status, setStatus] = useState<'PAGADO' | 'PENDIENTE'>('PAGADO');
    const [documentFile, setDocumentFile] = useState<SalesDocumentFileRequest | null>(null);
    const [rideFile, setRideFile] = useState<SalesDocumentFileRequest | null>(null);
    const [payPhoneContract, setPayPhoneContract] = useState<ContractExpiring | null>(null);
    const [payPhoneClientData, setPayPhoneClientData] = useState<ClientData | null>(null);
    const [payPhoneUrl, setPayPhoneUrl] = useState<string>('');
    const [paymentActionRow, setPaymentActionRow] = useState<OrganizationBillingRow | null>(null);
    const [activeCardToken, setActiveCardToken] = useState<OrganizationCardToken | null>(null);
    const [cardTokens, setCardTokens] = useState<OrganizationCardToken[]>([]);
    const [managementPlanId, setManagementPlanId] = useState<string>('');
    const [managementBillingPeriods, setManagementBillingPeriods] = useState<number>(1);
    const [isRegisterModalOpen, setIsRegisterModalOpen] = useState<boolean>(false);
    const [registerResult, setRegisterResult] = useState<OnboardingRegisterResponse | null>(null);
    const [onboardingPayPhoneUrl, setOnboardingPayPhoneUrl] = useState<string>('');

    const calculateInvoiceTotal = useCallback((contract: ContractExpiring, periods: number, planId?: string | null) => {
        const selectedPlan = plans.find(plan => plan.id === planId);
        const discountPercentage = Number(contract.discountPercentage || 0);
        const unitPrice = selectedPlan
            ? Number(selectedPlan.price || 0) - (Number(selectedPlan.price || 0) * discountPercentage / 100)
            : Number(contract.totalPrice || 0);

        return unitPrice * Number(periods || 1);
    }, [plans]);

    const totalToInvoice = useMemo(() => {
        if (!selectedContract) {
            return 0;
        }

        return calculateInvoiceTotal(selectedContract, billingPeriods, selectedPlanId || selectedContract.planId);
    }, [selectedContract, billingPeriods, selectedPlanId, calculateInvoiceTotal]);

    const paymentTotal = useMemo(() => {
        return paymentRows.reduce((total, row) => total + Number(row.amount || 0), 0);
    }, [paymentRows]);

    const pendingAmount = useMemo(() => totalToInvoice - paymentTotal, [totalToInvoice, paymentTotal]);

    const selectedBillingPlan = useMemo(() => {
        if (!selectedContract) {
            return null;
        }

        return plans.find(plan => plan.id === (selectedPlanId || selectedContract.planId)) || null;
    }, [plans, selectedContract, selectedPlanId]);
    const isFreeBillingPlan = !!selectedBillingPlan && Number(selectedBillingPlan.price || 0) <= 0;

    const paidPlans = useMemo(() => {
        return plans.filter(plan => Number(plan.price || 0) > 0 && String(plan.type || '').toUpperCase() !== 'FREE');
    }, [plans]);

    const activePlans = useMemo(() => {
        return plans.filter(plan => plan.status === 'ACTIVO');
    }, [plans]);

    const managementSelectedPlan = useMemo(() => {
        return plans.find(plan => plan.id === managementPlanId) || null;
    }, [plans, managementPlanId]);

    const managementOperationTotal = useMemo(() => {
        if (!selectedOrganizationManagement?.hasContract) {
            return 0;
        }

        const contract = selectedOrganizationManagement.nextContract;
        return calculateInvoiceTotal(contract, managementBillingPeriods, managementPlanId || contract.planId);
    }, [selectedOrganizationManagement, managementBillingPeriods, managementPlanId, calculateInvoiceTotal]);

    const isManagementFreePlan = !!managementSelectedPlan && Number(managementSelectedPlan.price || 0) <= 0;

    const filteredSalesDocuments = useMemo(() => {
        if (salesDocumentStatusFilter === 'TODOS') {
            return salesDocuments;
        }

        return salesDocuments.filter(document => document.status === salesDocumentStatusFilter);
    }, [salesDocuments, salesDocumentStatusFilter]);

    const organizationRows = useMemo<OrganizationBillingRow[]>(() => {
        const groups = new Map<string, ContractExpiring[]>();

        contracts.forEach(contract => {
            const current = groups.get(contract.organizationId) || [];
            current.push(contract);
            groups.set(contract.organizationId, current);
        });

        const rows = Array.from(groups.values()).map(group => {
            const nextContract = getCurrentOrganizationContract(group);

            return {
                organizationId: nextContract.organizationId,
                organizationName: nextContract.organizationName,
                organizationPhone: nextContract.organizationPhone,
                phone: nextContract.phone,
                billingEmail: nextContract.billingEmail,
                contracts: sortContractsDescending(group),
                nextContract,
                hasContract: true
            };
        });

        if (contractStatus === 'TODOS') {
            organizationOptions.forEach(organization => {
                if (groups.has(organization.id) || rows.some(row => row.organizationId === organization.id)) {
                    return;
                }

                const pendingContract = createPendingOrganizationContract(organization);
                rows.push({
                    organizationId: organization.id,
                    organizationName: organization.name,
                    organizationPhone: organization.phone,
                    phone: organization.phone,
                    billingEmail: organization.email,
                    contracts: [],
                    nextContract: pendingContract,
                    hasContract: false
                });
            });
        }

        return rows.sort((a, b) => {
            const missingContractCompare = Number(b.hasContract) - Number(a.hasContract);
            if (missingContractCompare !== 0) {
                return missingContractCompare;
            }

            const activeCompare = Number(b.nextContract.status === 'ACTIVO') - Number(a.nextContract.status === 'ACTIVO');
            if (activeCompare !== 0) {
                return activeCompare;
            }

            const trialCompare = Number(isTrialContract(b.nextContract)) - Number(isTrialContract(a.nextContract));
            if (trialCompare !== 0) {
                return trialCompare;
            }

            const endCompare = new Date(a.nextContract.endDate).getTime() - new Date(b.nextContract.endDate).getTime();
            if (endCompare !== 0) {
                return endCompare;
            }

            return a.organizationName.localeCompare(b.organizationName);
        });
    }, [contracts, contractStatus, organizationOptions]);

    const isPlanChange = useMemo(() => {
        return !!selectedContract
            && !!selectedPlanId
            && selectedPlanId !== selectedContract.planId;
    }, [selectedContract, selectedPlanId]);

    const currentPlanPrice = selectedContract ? Number(selectedContract.totalPrice || 0) : 0;
    const newPlanPrice = selectedContract
        ? calculateInvoiceTotal(selectedContract, 1, selectedPlanId || selectedContract.planId)
        : 0;
    const registerPlanId = Form.useWatch('planId', registerForm);
    const registerSelectedPlan = plans.find(plan => plan.id === registerPlanId) || null;
    const registerIsClinic = isClinicPlan(registerSelectedPlan);
    const registerClinicSpecialityIds: string[] = Form.useWatch('clinicSpecialtyIds', registerForm) || [];
    const registerClinicCenters: any[] = Form.useWatch('clinicCenters', registerForm) || [];
    const registerDoctorCapacity = registerSelectedPlan?.rolSystems?.find(role => role.rolSystemId === doctorRolSystemId)?.maxUsers ?? null;
    const registerNurseCapacity = registerSelectedPlan?.rolSystems?.find(role => role.rolSystemId === nurseRolSystemId)?.maxUsers ?? null;
    const registerMaxSpecialities = registerSelectedPlan?.featureFlagsResponse?.maxSpecialities ?? null;
    const registerMaxCenters = registerSelectedPlan?.featureFlagsResponse?.maxCenters ?? null;
    const registerClinicSpecialities = specialities.filter(speciality =>
        registerClinicSpecialityIds.includes(speciality.id || ''));
    const registerRequiresCardValidation = !!registerSelectedPlan
        && Number(registerSelectedPlan.price || 0) > 0
        && String(registerSelectedPlan.type || '').toUpperCase() !== 'FREE';
    const registerPayPhoneInitialAmount = getPlanPayPhoneInitialAmount(registerSelectedPlan);

    const loadContracts = async () => {
        const response = await props.getContractsExpiring(days, organizationId || undefined, contractStatus);
        setContracts(filterContractsByStatus(response || [], contractStatus));
    };

    const handleClearFilters = async () => {
        setDays(null);
        setOrganizationId('');
        setContractStatus('TODOS');
        await handleSearchOrganizations('');
        const response = await props.getContractsExpiring(undefined, undefined);
        setContracts(response || []);
    };

    const handleSearchOrganizations = async (value: string) => {
        const response = await props.searchOrganizations(value || '', 20);
        setOrganizationOptions(response || []);
    };

    const loadPaymentMethods = async () => {
        const response = await props.getOrganizationPaymentMethod();
        setPaymentMethods(response || []);
    };

    const loadPlans = async () => {
        const response = await props.getPlans();
        const activePlans = (response || []).filter((plan: Plan) => plan.status === 'ACTIVO');
        setPlans(activePlans);
        return activePlans;
    };

    const loadOnboardingCatalogs = async () => {
        const [specialityResponse, sectionResponse, legalResponse] = await Promise.all([
            props.getActiveSpeciality(),
            props.getMedicalRecordSections(),
            props.getLegalDocuments()
        ]);
        const activeSpecialities = (specialityResponse || []).filter((speciality: Speciality) => !speciality.status || speciality.status === 'ACTIVO');
        const activeSections = (sectionResponse || []).filter((section: MedicalRecordSection) => !section.status || section.status === 'ACTIVO');
        const currentDocuments = (legalResponse || []).filter((document: LegalDocument) => document.status === 'ACTIVO' && document.current);

        setSpecialities(activeSpecialities);
        setMedicalRecordSections(activeSections);
        setOnboardingLegalDocuments(currentDocuments);

        return {
            specialities: activeSpecialities,
            sections: activeSections,
            legalDocuments: currentDocuments
        };
    };

    const getDefaultSectionIds = (speciality?: Speciality | null, sections: MedicalRecordSection[] = medicalRecordSections) => {
        const specialitySections = (speciality?.defaultSections || [])
            .map(section => section.id)
            .filter((id): id is string => !!id);

        if (specialitySections.length > 0) {
            return specialitySections;
        }

        return sections
            .map(section => section.id)
            .filter((id): id is string => !!id);
    };

    const openRegisterCompanyModal = async () => {
        setRegisterResult(null);
        setOnboardingPayPhoneUrl('');
        registerForm.resetFields();

        const activePlans = plans.length > 0 ? plans : await loadPlans();
        const catalogs = await loadOnboardingCatalogs();
        const defaultPlan = activePlans.find((plan: Plan) => String(plan.type || '').toUpperCase() === 'FREE')
            || activePlans[0];
        const defaultSpeciality = catalogs.specialities.find((speciality: Speciality) => speciality.name === 'Medicina General')
            || catalogs.specialities[0];

        registerForm.setFieldsValue({
            planId: defaultPlan?.id,
            specialtyId: defaultSpeciality?.id,
            clinicSpecialtyIds: defaultSpeciality?.id ? [defaultSpeciality.id] : [],
            clinicCenters: [{ name: '', address: '', latitud: '', longitude: '' }],
            clinicDoctors: [],
            clinicNurses: [],
            medicalRecordSectionIds: getDefaultSectionIds(defaultSpeciality, catalogs.sections),
            legalDocuments: catalogs.legalDocuments
                .filter((document: LegalDocument) => !document.optional)
                .map((document: LegalDocument) => document.id)
                .filter((id: string | null | undefined): id is string => !!id),
            startTime: '08:00',
            endTime: '17:00',
            slotMinutes: 30,
            clientAddress: 'No registrado'
        });

        setIsRegisterModalOpen(true);
    };

    const handleRegisterPlanChange = (planId: string) => {
        const selectedPlan = plans.find(plan => plan.id === planId);
        const currentSpecialityId = registerForm.getFieldValue('specialtyId');

        if (isClinicPlan(selectedPlan)) {
            const currentClinicSpecialityIds = registerForm.getFieldValue('clinicSpecialtyIds') || [];
            registerForm.setFieldsValue({
                clinicSpecialtyIds: Array.from(new Set([
                    ...currentClinicSpecialityIds,
                    currentSpecialityId
                ].filter(Boolean))),
                clinicCenters: registerForm.getFieldValue('clinicCenters')?.length
                    ? registerForm.getFieldValue('clinicCenters')
                    : [{ name: '', address: '', latitud: '', longitude: '' }],
                clinicDoctors: registerForm.getFieldValue('clinicDoctors') || [],
                clinicNurses: registerForm.getFieldValue('clinicNurses') || [],
                medicalRecordSectionIds: []
            });
            return;
        }

        const selectedSpeciality = specialities.find(item => item.id === currentSpecialityId);
        registerForm.setFieldsValue({
            medicalRecordSectionIds: getDefaultSectionIds(selectedSpeciality)
        });
    };

    const handleRegisterSpecialityChange = (specialityId: string) => {
        const speciality = specialities.find(item => item.id === specialityId);
        registerForm.setFieldsValue({
            medicalRecordSectionIds: getDefaultSectionIds(speciality)
        });
    };

    const applyTestOrganizationData = () => {
        const stamp = new Date()
            .toISOString()
            .replace(/\D/g, '')
            .slice(4, 14);
        const testName = `PRUEBA ${stamp}`;
        const testEmail = `prueba.${stamp}@thebluecoding.com`;
        const testPhone = '+593984094602';
        const testIdentification = `17${stamp.slice(-8)}`;

        registerForm.setFieldsValue({
            firstName: 'PRUEBA',
            secondName: 'PAYPHONE',
            lastName: stamp,
            secondLastName: 'TBC',
            licenseNumber: testIdentification,
            email: testEmail,
            phone: testPhone,
            password: `TbcTest${stamp}$`,
            clientRuc: testIdentification,
            clientName: testName,
            clientEmail: testEmail,
            clientPhone: testPhone,
            clientAddress: 'Conocoto, Quito',
            startTime: '08:00',
            endTime: '17:00',
            slotMinutes: 30
        });
    };

    const handleTestOrganizationChange = (checked: boolean) => {
        if (checked) {
            applyTestOrganizationData();
        }
    };

    const syncRegisterBillingData = (_: any, allValues: any) => {
        const fullName = [allValues.firstName, allValues.secondName, allValues.lastName, allValues.secondLastName]
            .filter(Boolean)
            .join(' ')
            .trim();

        registerForm.setFieldsValue({
            clientRuc: allValues.clientRuc || allValues.licenseNumber,
            clientName: allValues.clientName || fullName,
            clientEmail: allValues.clientEmail || allValues.email,
            clientPhone: allValues.clientPhone || allValues.phone
        });
    };

    const handleOnboardingRegister = async () => {
        let values: any;

        try {
            values = await registerForm.validateFields();
        } catch {
            return;
        }

        const clinic = registerIsClinic;
        const clinicSpecialityIds = Array.from(new Set<string>([
            ...(values.clinicSpecialtyIds || [])
        ].filter(Boolean)));
        const clinicDoctors = values.clinicDoctors || [];
        const clinicNurses = values.clinicNurses || [];

        if (clinic) {
            if (registerMaxSpecialities !== null && clinicSpecialityIds.length > registerMaxSpecialities) {
                Modal.warning({
                    title: 'Limite de especialidades del plan',
                    content: `El plan permite máximo ${registerMaxSpecialities} especialidades.`
                });
                return;
            }

            if (registerMaxCenters !== null && (values.clinicCenters || []).length > registerMaxCenters) {
                Modal.warning({
                    title: 'Limite de centros del plan',
                    content: `El plan permite máximo ${registerMaxCenters} centros médicos.`
                });
                return;
            }

            if (registerNurseCapacity !== null && registerNurseCapacity > 0 && clinicNurses.length > registerNurseCapacity) {
                Modal.warning({
                    title: 'Limite de enfermeros del plan',
                    content: `El plan permite máximo ${registerNurseCapacity} enfermeros.`
                });
                return;
            }

            if (registerDoctorCapacity !== null && registerDoctorCapacity > 0 && clinicDoctors.length > registerDoctorCapacity) {
                Modal.warning({
                    title: 'Limite de médicos del plan',
                    content: `El plan permite máximo ${registerDoctorCapacity} médicos.`
                });
                return;
            }
        }

        const clientData: ClientData = {
            ruc: values.clientRuc || values.licenseNumber,
            name: values.clientName || [values.firstName, values.lastName].filter(Boolean).join(' '),
            email: values.clientEmail || values.email,
            phone: values.clientPhone || values.phone,
            address: values.clientAddress || 'No registrado'
        };

        const response = await props.postOnboardingRegister({
            firstName: values.firstName,
            secondName: values.secondName || '',
            lastName: values.lastName,
            secondLastName: values.secondLastName || '',
            email: values.email,
            password: values.password,
            phone: values.phone,
            licenseNumber: values.licenseNumber,
            specialtyId: clinic ? (clinicSpecialityIds[0] || undefined) : values.specialtyId,
            establishmentServiceId: clinic ? getPlanEstablishmentServiceId(registerSelectedPlan) : undefined,
            clinicSpecialityIds: clinic ? clinicSpecialityIds : [],
            centers: clinic
                ? (values.clinicCenters || []).map((center: any) => ({
                    name: center.name,
                    address: center.address,
                    latitud: center.latitud || '',
                    longitude: center.longitude || ''
                }))
                : [],
            doctors: clinic
                ? clinicDoctors.map((doctor: any) => ({
                    firstName: doctor.firstName,
                    secondName: doctor.secondName || '',
                    lastName: doctor.lastName,
                    secondLastName: doctor.secondLastName || '',
                    email: doctor.email,
                    phone: doctor.phone,
                    licenseNumber: doctor.licenseNumber,
                    specialtyId: doctor.specialtyId,
                    schedules: [{
                        centerIndex: Number(doctor.centerIndex || 0),
                        weekDayIds: doctor.weekDayIds || defaultWeekDayIds,
                        startTime: doctor.startTime || '08:00',
                        endTime: doctor.endTime || '17:00',
                        slotMinutes: Number(doctor.slotMinutes || 30)
                    }]
                }))
                : [],
            nurses: clinic
                ? clinicNurses.map((nurse: any) => ({
                    firstName: nurse.firstName,
                    secondName: nurse.secondName || '',
                    lastName: nurse.lastName,
                    secondLastName: nurse.secondLastName || '',
                    email: nurse.email,
                    phone: nurse.phone || ''
                }))
                : [],
            medicalRecordSectionIds: clinic ? [] : values.medicalRecordSectionIds || [],
            schedules: clinic ? [] : [
                {
                    weekDayIds: defaultWeekDayIds,
                    startTime: values.startTime || '08:00',
                    endTime: values.endTime || '17:00',
                    slotMinutes: Number(values.slotMinutes || 30)
                }
            ],
            planId: values.planId,
            sequences: defaultSequences,
            LegalDocuments: values.legalDocuments || [],
            clientData
        });

        if (!response) {
            return;
        }

        setRegisterResult(response);

        if (response.requiresCardValidation && response.payPhone?.clientTransactionId) {
            localStorage.setItem(pendingPayPhoneStorageKey, JSON.stringify({
                mode: 'trial',
                organizationId: response.organizationId,
                planId: values.planId,
                clientTransactionId: response.payPhone.clientTransactionId,
                clientData
            }));

            const payPhoneUrl = response.payPhone.payWithCard || response.payPhone.payWithPayPhone || '';
            setOnboardingPayPhoneUrl(payPhoneUrl);
            setIsRegisterModalOpen(false);
        }

        await handleSearchOrganizations('');
        await loadContracts();
    };

    const openRegisterPayPhoneLink = () => {
        const url = registerResult?.payPhone?.payWithCard || registerResult?.payPhone?.payWithPayPhone;

        if (url) {
            window.open(url, '_blank');
        }
    };

    const loadSalesDocuments = async (contractId: string) => {
        const response = await props.getContractSalesDocuments(contractId);
        setSalesDocuments(response || []);
    };

    const setDefaultPayment = (contract: ContractExpiring, periods: number, planId?: string | null) => {
        if (status !== 'PAGADO' || paymentMethods.length === 0) {
            setPaymentRows([]);
            return;
        }

        setPaymentRows([{
            key: `${paymentMethods[0].id}-${Date.now()}`,
            organizationPaymentMethodId: paymentMethods[0].id,
            amount: calculateInvoiceTotal(contract, periods, planId || contract.planId)
        }]);
    };

    useEffect(() => {
        loadContracts();
        loadPaymentMethods();
        loadPlans();
        handleSearchOrganizations('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selectedContract) {
            setDefaultPayment(selectedContract, billingPeriods, selectedPlanId || selectedContract.planId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [billingPeriods, selectedContract, selectedPlanId, paymentMethods, plans]);

    const handleSelectContract = (contract: ContractExpiring, options?: { planId?: string; periods?: number }) => {
        const planId = options?.planId || contract.planId;
        const periods = options?.periods || 1;

        setSelectedContract(contract);
        setIsBillingModalOpen(true);
        setBillingPeriods(periods);
        setSelectedPlanId(planId);
        setStatus('PAGADO');
        setSalesDocumentStatusFilter('PAGADO');
        setDocumentFile(null);
        setRideFile(null);
        setSalesDocuments([]);
        form.resetFields();
        form.setFieldsValue({
            number: contract.lastSalesDocumentNumber || '',
            planId,
            billingPeriods: periods,
            status: 'PAGADO',
            statusSri: 'MANUAL',
            integrationSri: true,
            ruc: '',
            name: contract.organizationName,
            email: contract.billingEmail,
            phone: '',
            address: '',
            authorization: '',
            rideFile: null
        });
        setDefaultPayment(contract, periods, planId);
        loadSalesDocuments(contract.contractId);
    };

    const handleSearchClientData = async () => {
        const ruc = String(form.getFieldValue('ruc') || '').trim();
        if (!ruc) {
            form.setFieldsValue(emptyClientData);
            return;
        }

        const response = await props.getClientData(ruc);
        if (response && response.ruc) {
            form.setFieldsValue(response);
            return;
        }

        form.setFieldsValue({
            ...emptyClientData,
            ruc
        });
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>, fileType: 'document' | 'ride') => {
        const file = event.target.files && event.target.files[0];
        if (!file) {
            if (fileType === 'document') {
                setDocumentFile(null);
            } else {
                setRideFile(null);
                form.setFieldValue('rideFile', null);
            }
            return;
        }

        const value = await fileToBase64(file);
        if (fileType === 'document') {
            setDocumentFile(value);
        } else {
            setRideFile(value);
            form.setFieldValue('rideFile', value.fileName);
            form.validateFields(['rideFile']);
        }
    };

    const addPaymentRow = () => {
        if (paymentMethods.length === 0) {
            return;
        }

        setPaymentRows(current => [
            ...current,
            {
                key: `${paymentMethods[0].id}-${Date.now()}`,
                organizationPaymentMethodId: paymentMethods[0].id,
                amount: 0
            }
        ]);
    };

    const updatePaymentRow = (key: string, field: keyof PaymentRow, value: string | number | null) => {
        setPaymentRows(current => current.map(row => {
            if (row.key !== key) {
                return row;
            }

            return {
                ...row,
                [field]: field === 'amount' ? Number(value || 0) : String(value || '')
            };
        }));
    };

    const removePaymentRow = (key: string) => {
        setPaymentRows(current => current.filter(row => row.key !== key));
    };

    const handleStatusChange = (value: 'PAGADO' | 'PENDIENTE') => {
        setStatus(value);
        if (value === 'PENDIENTE') {
            setPaymentRows([]);
            return;
        }

        if (selectedContract) {
            setDefaultPayment(selectedContract, billingPeriods, selectedPlanId || selectedContract.planId);
        }
    };

    const handleSubmit = async () => {
        if (!selectedContract) {
            return;
        }

        let values: any;
        try {
            values = isFreeBillingPlan
                ? await form.validateFields(['planId'])
                : await form.validateFields();
        } catch {
            return;
        }

        if (isFreeBillingPlan) {
            const response = await props.postContract({
                organizationId: selectedContract.organizationId,
                planId: values.planId || selectedContract.planId,
                previousContractId: selectedContract.contractId,
                startDate: new Date().toISOString(),
                agreedPrice: 0,
                discountPercentage: 0,
                autoRenew: false,
                renewalPeriodMonths: null,
                signedAt: null,
                signedBy: null,
                terminationDate: null,
                termsConditions: null,
                notes: `Contrato gratis generado desde facturacion. Contrato anterior #${selectedContract.contractNumber}`
            });

            if (response) {
                await loadContracts();
                setIsBillingModalOpen(false);
                setSelectedContract(null);
            }
            return;
        }

        const normalizedPayments: SalesDocumentPaymentMethodRequest[] = status === 'PAGADO'
            ? paymentRows.map(row => ({
                organizationPaymentMethodId: row.organizationPaymentMethodId,
                amount: Number(row.amount || 0)
            }))
            : [];

        if (status === 'PAGADO') {
            if (normalizedPayments.length === 0) {
                form.setFields([
                    {
                        name: 'paymentSummary',
                        errors: ['Agregue al menos un metodo de pago.']
                    }
                ]);
                return;
            }

            if (normalizedPayments.some(payment => !payment.organizationPaymentMethodId || Number(payment.amount || 0) <= 0)) {
                form.setFields([
                    {
                        name: 'paymentSummary',
                        errors: ['Cada metodo de pago debe tener un valor mayor a 0.']
                    }
                ]);
                return;
            }

            if (Math.abs(paymentTotal - totalToInvoice) > 0.01) {
                form.setFields([
                    {
                        name: 'paymentSummary',
                        errors: ['La suma de los metodos de pago debe cuadrar exactamente con el total a facturar.']
                    }
                ]);
                return;
            }
        }

        const request: ContractSalesDocumentRequest = {
            number: values.number,
            planId: values.planId || selectedContract.planId,
            billingPeriods: Number(values.billingPeriods || 1),
            clientData: {
                id: values.id || null,
                ruc: values.ruc,
                name: values.name,
                email: values.email,
                phone: values.phone,
                address: values.address
            },
            status,
            statusSri: 'MANUAL',
            integrationSri: true,
            authorization: values.authorization || null,
            documentFile,
            rideFile,
            paymentMethods: normalizedPayments
        };

        const response = await props.postContractSalesDocument(selectedContract.contractId, request);
        if (response) {
            await loadContracts();
            await loadSalesDocuments(selectedContract.contractId);
            setIsBillingModalOpen(false);
            setSelectedContract(null);
        }
    };

    const loadCardTokenAdministration = async (organizationId: string) => {
        const [token, tokens]: [OrganizationCardToken | undefined, OrganizationCardToken[] | undefined] = await Promise.all([
            props.getActiveOrganizationCardToken(organizationId),
            props.getOrganizationCardTokens(organizationId)
        ]);

        const activeToken = token?.id ? token : null;
        setActiveCardToken(activeToken);
        setCardTokens(Array.isArray(tokens) ? tokens : []);
        return activeToken;
    };

    const handleOpenPayPhonePayment = async (contract: ContractExpiring) => {
        const savedClientData: ClientData | undefined = await props.getOrganizationClientData(contract.organizationId);
        const clientData: ClientData = {
            id: savedClientData?.id || null,
            ruc: savedClientData?.ruc || '',
            name: savedClientData?.name || contract.organizationName || '',
            email: savedClientData?.email || contract.billingEmail || '',
            phone: savedClientData?.phone || contract.organizationPhone || contract.phone || '',
            address: savedClientData?.address || ''
        };

        setPayPhoneContract(contract);
        setPayPhoneClientData(clientData);
        setPayPhoneUrl('');
        await loadCardTokenAdministration(contract.organizationId);
    };

    const handleContinueManualBilling = () => {
        if (!paymentActionRow?.hasContract) {
            return;
        }

        const contract = paymentActionRow.nextContract;
        setPaymentActionRow(null);
        handleSelectContract(contract);
    };

    const handleContinueAutomaticBilling = async () => {
        if (!paymentActionRow) {
            return;
        }

        const contract = paymentActionRow.nextContract;
        setPaymentActionRow(null);
        await handleOpenPayPhonePayment(contract);
    };

    const handlePayPhonePayment = async () => {
        if (!payPhoneContract) {
            return;
        }

        const clientData: ClientData = {
            id: payPhoneClientData?.id || null,
            ruc: payPhoneClientData?.ruc || activeCardToken?.document || '',
            name: payPhoneClientData?.name || activeCardToken?.cardHolderName || payPhoneContract.organizationName || '',
            email: payPhoneClientData?.email || activeCardToken?.email || payPhoneContract.billingEmail || '',
            phone: payPhoneClientData?.phone || activeCardToken?.phone || payPhoneContract.organizationPhone || payPhoneContract.phone || '',
            address: payPhoneClientData?.address || ''
        };

        const request = {
            source: 'PORTAL_TBC',
            reference: `Validacion tarjeta ${payPhoneContract.organizationName}`,
            clientData
        };

        const response: PayPhonePrepareResponse | undefined = await props.prepareOrganizationCardTokenPayPhone(payPhoneContract.organizationId, request);
        const payPhoneUrl = response?.payWithCard || response?.payWithPayPhone;

        if (!payPhoneUrl) {
            Modal.warning({
                title: 'PayPhone',
                content: 'PayPhone no devolvio un link de pago.'
            });
            return;
        }

        localStorage.setItem(pendingPayPhoneStorageKey, JSON.stringify({
            mode: 'cardtoken',
            organizationId: payPhoneContract.organizationId,
            clientTransactionId: response.clientTransactionId,
            clientData
        }));

        setPayPhoneUrl(payPhoneUrl);
    };

    const handleDisableActiveCardToken = () => {
        const targetOrganizationId = payPhoneContract?.organizationId || selectedOrganizationManagement?.organizationId;
        if (!activeCardToken?.id || !targetOrganizationId) {
            return;
        }

        Modal.confirm({
            title: 'Desactivar tarjeta',
            content: 'La tarjeta actual quedara inactiva para cobros automaticos. El cliente podra validar otra tarjeta con PayPhone cuando sea necesario.',
            okText: 'Desactivar',
            cancelText: 'Cancelar',
            okButtonProps: { danger: true },
            onOk: async () => {
                const response = await props.disableOrganizationCardToken(activeCardToken.id);
                if (response) {
                    await loadCardTokenAdministration(targetOrganizationId);
                }
            }
        });
    };

    const handleContinueManagementManualBilling = () => {
        const selectedPlan = plans.find(item => item.id === managementPlanId);
        if (!selectedPlan) {
            Modal.warning({
                title: 'Seleccione un plan',
                content: 'Debe elegir el plan que se va a registrar manualmente.'
            });
            return;
        }

        if (!selectedOrganizationManagement?.hasContract) {
            Modal.confirm({
                title: 'Generar contrato manual',
                content: `Se generara un contrato para ${selectedOrganizationManagement?.organizationName || 'la organizacion'} con el plan ${selectedPlan.name}, sin validar tarjeta PayPhone.`,
                okText: 'Generar contrato',
                cancelText: 'Cancelar',
                onOk: async () => {
                    const response = await props.postContract({
                        organizationId: selectedOrganizationManagement?.organizationId,
                        planId: selectedPlan.id,
                        previousContractId: null,
                        startDate: new Date().toISOString(),
                        agreedPrice: Number(selectedPlan.price || 0),
                        discountPercentage: 0,
                        autoRenew: false,
                        renewalPeriodMonths: null,
                        signedAt: null,
                        signedBy: null,
                        terminationDate: null,
                        termsConditions: null,
                        notes: 'Contrato inicial generado manualmente desde gestión de contratos'
                    });

                    if (response) {
                        await loadContracts();
                        handleCloseOrganizationManagement();
                    }
                }
            });
            return;
        }

        const contract = selectedOrganizationManagement.nextContract;
        const planId = managementPlanId || contract.planId;
        const periods = managementBillingPeriods || 1;
        handleCloseOrganizationManagement();
        handleSelectContract(contract, { planId, periods });
    };

    const handleChargeManagementWithPayPhone = () => {
        if (!selectedOrganizationManagement?.hasContract) {
            Modal.warning({
                title: 'Sin contrato base',
                content: 'La organizacion todavia no tiene un contrato para usar cobro automatico.'
            });
            return;
        }

        const contract = selectedOrganizationManagement.nextContract;
        const plan = plans.find(item => item.id === (managementPlanId || contract.planId));
        if (!plan) {
            Modal.warning({
                title: 'Seleccione un plan',
                content: 'Debe elegir el plan que se va a cobrar o renovar.'
            });
            return;
        }

        if (Number(plan.price || 0) <= 0 || String(plan.type || '').toUpperCase() === 'FREE') {
            Modal.info({
                title: 'Plan gratuito',
                content: 'Los planes gratuitos se generan desde la opcion manual porque no requieren cobro PayPhone.'
            });
            return;
        }

        if (activeCardToken?.status !== 'ACTIVO') {
            Modal.warning({
                title: 'Primero valide la tarjeta',
                content: 'La organizacion necesita una tarjeta activa para realizar cobros automaticos. Valide o cambie la tarjeta desde la pestana Tarjetas.',
                onOk: () => setManagementActiveTab('cards')
            });
            return;
        }

        const periods = managementBillingPeriods || 1;
        const total = calculateInvoiceTotal(contract, periods, plan.id);
        Modal.confirm({
            title: 'Cobro automatico PayPhone',
            content: `Se cobrara ${money(total, contract.currency || 'USD')} por ${periods} periodo(s) del plan ${plan.name}. Si PayPhone aprueba el pago, se generara o renovara el contrato y la factura quedara pendiente de emision manual.`,
            okText: 'Cobrar con token',
            cancelText: 'Cancelar',
            onOk: async () => {
                const response = await props.chargeContractWithCardToken(contract.contractId, {
                    planId: plan.id,
                    billingPeriods: periods,
                    force: true
                });

                if (response) {
                    Modal.success({
                        title: 'Cobro aprobado',
                        content: response.message || 'PayPhone aprobo el cobro y el contrato fue actualizado.'
                    });
                    await loadContracts();
                    await loadCardTokenAdministration(contract.organizationId);
                }
            }
        });
    };

    const handleCancelSalesDocument = (document: OrganizationSalesDocument) => {
        Modal.confirm({
            title: 'Anular factura',
            content: `La factura ${document.number} quedara marcada como anulada. Esta accion no elimina el registro.`,
            okText: 'Anular',
            cancelText: 'Cancelar',
            okButtonProps: { danger: true },
            onOk: async () => {
                const response = await props.cancelSalesDocument(document.id);
                if (response) {
                    setSalesDocuments(current => current.map(item => item.id === document.id ? response : item));
                    if (selectedContract) {
                        await loadSalesDocuments(selectedContract.contractId);
                    }
                    await loadContracts();
                }
            }
        });
    };

    const currentAcceptedLegalDocuments = useMemo(() => {
        return legalDocuments.filter(document => document.current && document.status === 'ACTIVO');
    }, [legalDocuments]);

    const pendingLegalDocuments = useMemo(() => {
        const acceptedIds = new Set(currentAcceptedLegalDocuments.map(document => document.legalDocumentId));
        return currentLegalDocuments.filter(document =>
            document.current
            && document.status === 'ACTIVO'
            && document.id
            && !acceptedIds.has(document.id));
    }, [currentAcceptedLegalDocuments, currentLegalDocuments]);

    const managementAcceptedLegalDocuments = useMemo(() => {
        return managementLegalDocuments.filter(document => document.current && document.status === 'ACTIVO');
    }, [managementLegalDocuments]);

    const managementPendingLegalDocuments = useMemo(() => {
        const acceptedIds = new Set(managementAcceptedLegalDocuments.map(document => document.legalDocumentId));
        return managementCurrentLegalDocuments.filter(document =>
            document.current
            && document.status === 'ACTIVO'
            && document.id
            && !acceptedIds.has(document.id));
    }, [managementAcceptedLegalDocuments, managementCurrentLegalDocuments]);

    const findContractPaymentRequest = (contract: ContractExpiring) => {
        return managementPaymentRequests.find(paymentRequest =>
            paymentRequest.generatedContractId === contract.contractId
            && String(paymentRequest.provider || '').toUpperCase() === 'PAYPHONE');
    };

    const canReverseContractPayment = (contract: ContractExpiring) => {
        const paymentRequest = findContractPaymentRequest(contract);
        return !!paymentRequest
            && paymentRequest.status === 'APROBADO'
            && paymentRequest.invoiceStatus === 'PENDIENTE'
            && !paymentRequest.salesDocumentId;
    };

    const loadManagementLegalDocuments = async (row: OrganizationBillingRow) => {
        const [acceptedResponse, currentResponse] = await Promise.all([
            props.getOrganizationLegalDocuments(row.organizationId),
            props.getLegalDocuments()
        ]);
        setManagementLegalDocuments(acceptedResponse || []);
        setManagementCurrentLegalDocuments(currentResponse || []);
    };

    const loadManagementPaymentRequests = async (organizationId: string) => {
        const response = await props.getOrganizationPaymentRequests(organizationId);
        setManagementPaymentRequests(response || []);
    };

    const handleOpenOrganizationManagement = async (row: OrganizationBillingRow) => {
        setSelectedOrganizationManagement(row);
        setManagementActiveTab('summary');
        setManagementPlanId(row.nextContract?.planId || activePlans[0]?.id || '');
        setManagementBillingPeriods(1);
        setManagementLegalDocuments([]);
        setManagementCurrentLegalDocuments([]);
        setManagementPaymentRequests([]);
        setActiveCardToken(null);
        setCardTokens([]);
        await Promise.all([
            loadManagementLegalDocuments(row),
            loadManagementPaymentRequests(row.organizationId),
            loadCardTokenAdministration(row.organizationId)
        ]);
    };

    const handleCloseOrganizationManagement = () => {
        setSelectedOrganizationManagement(null);
        setManagementActiveTab('summary');
        setManagementLegalDocuments([]);
        setManagementCurrentLegalDocuments([]);
        setManagementPaymentRequests([]);
        setActiveCardToken(null);
        setCardTokens([]);
        setManagementPlanId('');
        setManagementBillingPeriods(1);
    };

    const renderManagementBillingOptions = (mode: 'manual' | 'automatic') => {
        if (!selectedOrganizationManagement) {
            return null;
        }

        const contract = selectedOrganizationManagement.nextContract;
        const selectedPlan = managementSelectedPlan || plans.find(plan => plan.id === contract.planId) || null;
        const currency = contract.currency || 'USD';

        return (
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Row gutter={[12, 12]}>
                    <Col xs={24} md={12}>
                        <Typography.Text strong>Plan a aplicar</Typography.Text>
                        <Select
                            style={{ width: '100%', marginTop: 6 }}
                            showSearch
                            value={managementPlanId || contract.planId || undefined}
                            placeholder="Seleccione el plan"
                            optionFilterProp="label"
                            options={activePlans.map(plan => ({
                                value: plan.id,
                                label: `${plan.name} - ${money(Number(plan.price || 0), currency)}`
                            }))}
                            onChange={value => setManagementPlanId(value)}
                        />
                    </Col>
                    <Col xs={24} md={6}>
                        <Typography.Text strong>Periodos</Typography.Text>
                        <InputNumber
                            style={{ width: '100%', marginTop: 6 }}
                            min={1}
                            max={60}
                            value={managementBillingPeriods}
                            onChange={value => setManagementBillingPeriods(Number(value || 1))}
                        />
                    </Col>
                    <Col xs={24} md={6}>
                        <Statistic
                            title={mode === 'manual' ? 'Valor manual' : 'Valor a cobrar'}
                            value={money(managementOperationTotal, currency)}
                        />
                    </Col>
                </Row>
                <Descriptions bordered size="small" column={1}>
                    <Descriptions.Item label="Organizacion">{selectedOrganizationManagement.organizationName}</Descriptions.Item>
                    <Descriptions.Item label="Contrato base">
                        {selectedOrganizationManagement.hasContract
                            ? `#${contract.contractNumber}`
                            : 'Sin contrato generado'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Plan actual">{contract.planName}</Descriptions.Item>
                    <Descriptions.Item label="Plan seleccionado">{selectedPlan?.name || 'Seleccione un plan'}</Descriptions.Item>
                    <Descriptions.Item label="Operacion">
                        {isManagementFreePlan
                            ? 'Se generara un contrato gratuito sin factura.'
                            : mode === 'manual'
                                ? 'Se registrara una factura manual o una renovacion con pago externo.'
                                : 'Se cobrara con el token PayPhone. La factura quedara pendiente de emision manual.'}
                    </Descriptions.Item>
                </Descriptions>
            </Space>
        );
    };

    const renderDocumentPreviewButton = (title: string, url?: string | null) => {
        if (!url) {
            return <Text type="secondary">No registrado</Text>;
        }

        return (
            <Button type="link" onClick={() => setDocumentPreview({ title, url })} style={{ padding: 0 }}>
                Visualizar
            </Button>
        );
    };

    const handleCancelContract = (contract: ContractExpiring) => {
        Modal.confirm({
            title: `Cancelar contrato #${contract.contractNumber}`,
            content: 'Si este contrato fue cobrado por PayPhone, primero se reversara el pago y luego se cancelara el contrato.',
            okText: 'Cancelar contrato',
            cancelText: 'Cerrar',
            okButtonProps: { danger: true },
            onOk: async () => {
                const response = await props.cancelContract(contract.contractId);
                if (response) {
                    await loadContracts();
                    if (selectedOrganizationManagement?.organizationId) {
                        await loadManagementPaymentRequests(selectedOrganizationManagement.organizationId);
                    }
                }
            }
        });
    };

    const organizationColumns = [
        {
            title: 'Organizacion',
            dataIndex: 'organizationName',
            key: 'organizationName',
            render: (value: string, row: OrganizationBillingRow) => {
                const phone = row.organizationPhone || row.phone || (row as any)?.organization?.phone;

                return (
                    <Space direction="vertical" size={0}>
                        <Text strong>{value}</Text>
                        <Text type="secondary">{row.billingEmail}</Text>
                        <Text type="secondary">{phone ? `Tel: ${phone}` : 'Tel: No registrado'}</Text>
                    </Space>
                );
            }
        },
        {
            title: 'Contrato vigente',
            dataIndex: 'nextContract',
            key: 'planName',
            render: (_: ContractExpiring, row: OrganizationBillingRow) => (
                <Space direction="vertical" size={0}>
                    <Text>{row.hasContract ? row.nextContract.planName : 'Sin contrato activo'}</Text>
                    {row.hasContract ? (
                        <Text type="secondary">Contrato #{row.nextContract.contractNumber}</Text>
                    ) : (
                        <Text type="secondary">Pendiente de validar tarjeta o generar contrato</Text>
                    )}
                    <Text type="secondary">{row.contracts.length} contrato(s) registrado(s)</Text>
                    {isTrialContract(row.nextContract) && (
                        <Tag color="blue">En prueba</Tag>
                    )}
                </Space>
            )
        },
        {
            title: 'Fin',
            dataIndex: 'nextContract',
            key: 'endDate',
            render: (_: ContractExpiring, row: OrganizationBillingRow) => (
                <Space direction="vertical" size={0}>
                    {row.hasContract ? (
                        <>
                            <Text>{formatCalendarDate(row.nextContract.endDate)}</Text>
                            <Tag color={row.nextContract.daysToExpire < 0 ? 'red' : 'gold'}>{row.nextContract.daysToExpire} dias</Tag>
                        </>
                    ) : (
                        <Text type="secondary">No aplica</Text>
                    )}
                </Space>
            )
        },
        {
            title: 'Estado',
            dataIndex: 'nextContract',
            key: 'status',
            render: (_: ContractExpiring, row: OrganizationBillingRow) => (
                <Space direction="vertical" size={0}>
                    <Tag color={getStatusColor(row.nextContract.status)}>{row.nextContract.status}</Tag>
                    {!row.hasContract && (
                        <Text type="secondary">Sin contrato generado</Text>
                    )}
                    {isTrialContract(row.nextContract) && row.nextContract.nextBillingAt && (
                        <Text type="secondary">Proximo cobro: {formatCalendarDate(row.nextContract.nextBillingAt)}</Text>
                    )}
                    {isTrialContract(row.nextContract) && row.nextContract.trialEndAt && (
                        <Text type="secondary">Prueba hasta: {formatCalendarDate(row.nextContract.trialEndAt)}</Text>
                    )}
                </Space>
            )
        },
        {
            title: 'Total',
            dataIndex: 'nextContract',
            key: 'totalPrice',
            align: 'right' as const,
            render: (_: ContractExpiring, row: OrganizationBillingRow) => money(row.nextContract.totalPrice, row.nextContract.currency || 'USD')
        },
        {
            title: 'Factura',
            dataIndex: 'nextContract',
            key: 'lastSalesDocumentStatus',
            render: (_: ContractExpiring, row: OrganizationBillingRow) => {
                if (!row.hasContract) {
                    return <Tag>No aplica</Tag>;
                }

                return row.nextContract.lastSalesDocumentStatus
                    ? <Tag color={getStatusColor(row.nextContract.lastSalesDocumentStatus)}>{row.nextContract.lastSalesDocumentStatus}</Tag>
                    : <Tag>Sin factura</Tag>;
            }
        },
        {
            title: '',
            key: 'actions',
            align: 'right' as const,
            render: (_: unknown, row: OrganizationBillingRow) => (
                <Button type="primary" icon={<FileSearchOutlined />} onClick={() => handleOpenOrganizationManagement(row)}>
                    Gestionar
                </Button>
            )
        }
    ];

    const organizationContractColumns = [
        {
            title: 'Plan',
            dataIndex: 'planName',
            key: 'planName',
            render: (value: string, row: ContractExpiring) => (
                <Space direction="vertical" size={0}>
                    <Text>{value}</Text>
                    <Text type="secondary">Contrato #{row.contractNumber}</Text>
                </Space>
            )
        },
        {
            title: 'Inicio',
            dataIndex: 'startDate',
            key: 'startDate',
            render: (value: string) => formatCalendarDate(value)
        },
        {
            title: 'Fin',
            dataIndex: 'endDate',
            key: 'endDate',
            render: (value: string, row: ContractExpiring) => (
                <Space direction="vertical" size={0}>
                    <Text>{formatCalendarDate(value)}</Text>
                    <Tag color={row.daysToExpire < 0 ? 'red' : 'gold'}>{row.daysToExpire} dias</Tag>
                </Space>
            )
        },
        {
            title: 'Estado',
            dataIndex: 'status',
            key: 'status',
            render: (value: string) => <Tag color={getStatusColor(value)}>{value}</Tag>
        },
        {
            title: 'Total',
            dataIndex: 'totalPrice',
            key: 'totalPrice',
            align: 'right' as const,
            render: (value: number, row: ContractExpiring) => money(value, row.currency || 'USD')
        },
        {
            title: 'Factura',
            dataIndex: 'lastSalesDocumentStatus',
            key: 'lastSalesDocumentStatus',
            render: (value: string | null) => value ? <Tag color={getStatusColor(value)}>{value}</Tag> : <Tag>Sin factura</Tag>
        },
        {
            title: '',
            key: 'actions',
            align: 'right' as const,
            render: (_: unknown, row: ContractExpiring) => (
                <Space>
                    {isTrialContract(row) && (
                        <Space direction="vertical" size={0} align="end">
                            <Tag color="blue">En prueba</Tag>
                            {row.nextBillingAt && (
                                <Text type="secondary">Cobro: {formatCalendarDate(row.nextBillingAt)}</Text>
                            )}
                        </Space>
                    )}
                    {!isTrialContract(row) && isPaidContract(row) && !hasRegisteredInvoice(row) && (
                        <Button icon={<FileSearchOutlined />} onClick={() => handleSelectContract(row)}>
                            Subir factura
                        </Button>
                    )}
                    {!isPaidContract(row) && <Tag>Plan gratis</Tag>}
                    {!isTrialContract(row) && isPaidContract(row) && hasRegisteredInvoice(row) && <Tag color="green">Factura registrada</Tag>}
                    {(row.status === 'ACTIVO' || canReverseContractPayment(row)) && (
                        <Button danger icon={<StopOutlined />} onClick={() => handleCancelContract(row)}>
                            {canReverseContractPayment(row)
                                ? 'Reversar / cancelar'
                                : 'Cancelar'}
                        </Button>
                    )}
                </Space>
            )
        }
    ];

    const salesDocumentColumns = [
        {
            title: 'Numero',
            dataIndex: 'number',
            key: 'number'
        },
        {
            title: 'Cliente',
            dataIndex: 'clientName',
            key: 'clientName',
            render: (value: string, row: OrganizationSalesDocument) => (
                <Space direction="vertical" size={0}>
                    <Text>{value}</Text>
                    <Text type="secondary">{row.clientRuc}</Text>
                </Space>
            )
        },
        {
            title: 'Estado',
            dataIndex: 'status',
            key: 'status',
            render: (value: string) => <Tag color={getStatusColor(value)}>{value}</Tag>
        },
        {
            title: 'Pago',
            dataIndex: 'paymentDate',
            key: 'paymentDate',
            render: (value: string | null) => value ? formatUtcDate(value) : ''
        },
        {
            title: 'Total',
            dataIndex: 'total',
            key: 'total',
            align: 'right' as const,
            render: (value: number, row: OrganizationSalesDocument) => money(value, row.currency || 'USD')
        },
        {
            title: '',
            key: 'actions',
            align: 'right' as const,
            render: (_: unknown, row: OrganizationSalesDocument) => (
                <Space>
                    <Button icon={<EyeOutlined />} onClick={() => setSelectedSalesDocument(row)}>
                        Ver
                    </Button>
                    {row.status !== 'ANULADO' && (
                        <Button danger icon={<StopOutlined />} onClick={() => handleCancelSalesDocument(row)}>
                            Anular
                        </Button>
                    )}
                </Space>
            )
        }
    ];

    return (
        <div className="tbc-admin-page">
            <Space direction="vertical" size={18} style={{ width: '100%' }}>
                <div>
                    <Title level={3} style={{ marginBottom: 4 }}>{moduleTitle}</Title>
                    <Text type="secondary">{moduleDescription}</Text>
                </div>

                <Tabs
                    destroyOnHidden
                    items={[
                        {
                            key: 'billing',
                            label: 'Contratos',
                            children: (
                                <>
                <Row justify="end">
                    <Button type="primary" icon={<PlusOutlined />} onClick={openRegisterCompanyModal}>
                        Registrar empresa
                    </Button>
                </Row>

                <Card>
                    <Row gutter={[12, 12]} align="top">
                        <Col xs={24} sm={12} lg={4}>
                            <Text strong>Dias a revisar</Text>
                            <InputNumber
                                min={0}
                                max={365}
                                value={days === null ? undefined : days}
                                placeholder="Todos"
                                onChange={(value) => setDays(value === null || value === undefined ? null : Number(value))}
                                style={{ width: '100%', marginTop: 6 }}
                            />
                            <Text type="secondary" style={{ fontSize: 12 }}>Vacio muestra todos los contratos.</Text>
                        </Col>
                        <Col xs={24} sm={12} lg={4}>
                            <Text strong>Estado</Text>
                            <Select
                                value={contractStatus}
                                onChange={(value) => setContractStatus(value)}
                                options={[
                                    { value: 'TODOS', label: 'Todos' },
                                    { value: 'ACTIVO', label: 'Activo' },
                                    { value: 'MORA', label: 'Mora' },
                                    { value: 'CANCELADO', label: 'Cancelado' }
                                ]}
                                style={{ width: '100%', marginTop: 6 }}
                            />
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                            <Text strong>Organizacion</Text>
                            <Select
                                showSearch
                                allowClear
                                value={organizationId || undefined}
                                filterOption={false}
                                onSearch={handleSearchOrganizations}
                                onChange={(value) => setOrganizationId(value || '')}
                                placeholder="Buscar por nombre, tenant, email o telefono"
                                options={organizationOptions.map(organization => ({
                                    value: organization.id,
                                    label: `${organization.name} - ${organization.tenant}`,
                                    organization
                                }))}
                                optionRender={(option) => {
                                    const organization = option.data.organization as OrganizationSearch;
                                    return (
                                        <Space direction="vertical" size={0}>
                                            <Text strong>{organization.name}</Text>
                                            <Text type="secondary">{organization.tenant} | {organization.email || organization.phone}</Text>
                                        </Space>
                                    );
                                }}
                                style={{ width: '100%', marginTop: 6 }}
                            />
                        </Col>
                        <Col xs={24} sm={8} lg={3}>
                            <Button icon={<SearchOutlined />} type="primary" block onClick={loadContracts} style={{ marginTop: 28 }}>
                                Buscar
                            </Button>
                        </Col>
                        <Col xs={24} sm={8} lg={3}>
                            <Button icon={<ReloadOutlined />} block onClick={loadContracts} style={{ marginTop: 28 }}>
                                Actualizar
                            </Button>
                        </Col>
                        <Col xs={24} sm={8} lg={3}>
                            <Button icon={<ClearOutlined />} block onClick={handleClearFilters} style={{ marginTop: 28 }}>
                                Limpiar
                            </Button>
                        </Col>
                    </Row>
                </Card>

                <Table
                    rowKey="organizationId"
                    columns={organizationColumns}
                    dataSource={organizationRows}
                    pagination={{ pageSize: 8 }}
                    scroll={{ x: 980 }}
                />

                <Drawer
                    open={!!selectedOrganizationManagement}
                    title={selectedOrganizationManagement ? `Gestion - ${selectedOrganizationManagement.organizationName}` : 'Gestion'}
                    placement="right"
                    width="100%"
                    onClose={handleCloseOrganizationManagement}
                    destroyOnHidden
                >
                    {selectedOrganizationManagement && (
                        <Space direction="vertical" size={18} style={{ width: '100%' }}>
                            <Row gutter={[12, 12]}>
                                <Col xs={24} md={8}>
                                    <Card size="small">
                                        <Statistic title="Organizacion" value={selectedOrganizationManagement.organizationName} />
                                        <Text type="secondary">{selectedOrganizationManagement.billingEmail}</Text>
                                        <br />
                                        <Text type="secondary">
                                            Tel: {selectedOrganizationManagement.organizationPhone || selectedOrganizationManagement.phone || 'No registrado'}
                                        </Text>
                                    </Card>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Card size="small">
                                        <Statistic
                                            title="Contrato vigente"
                                            value={selectedOrganizationManagement.hasContract ? selectedOrganizationManagement.nextContract.planName : 'Sin contrato'}
                                        />
                                        <Space wrap size={6}>
                                            {selectedOrganizationManagement.hasContract && (
                                                <Tag>Contrato #{selectedOrganizationManagement.nextContract.contractNumber}</Tag>
                                            )}
                                            <Tag color={getStatusColor(selectedOrganizationManagement.nextContract.status)}>
                                                {selectedOrganizationManagement.nextContract.status}
                                            </Tag>
                                            {isTrialContract(selectedOrganizationManagement.nextContract) && (
                                                <Tag color="blue">En prueba</Tag>
                                            )}
                                        </Space>
                                    </Card>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Card size="small">
                                        <Statistic
                                            title={isTrialContract(selectedOrganizationManagement.nextContract) ? 'Proximo cobro' : 'Fin del contrato'}
                                            value={formatCalendarDate(
                                                isTrialContract(selectedOrganizationManagement.nextContract) && selectedOrganizationManagement.nextContract.nextBillingAt
                                                    ? selectedOrganizationManagement.nextContract.nextBillingAt
                                                    : selectedOrganizationManagement.nextContract.endDate
                                            )}
                                        />
                                        <Text type="secondary">
                                            Total: {money(selectedOrganizationManagement.nextContract.totalPrice, selectedOrganizationManagement.nextContract.currency || 'USD')}
                                        </Text>
                                    </Card>
                                </Col>
                            </Row>

                            <Tabs
                                activeKey={managementActiveTab}
                                onChange={setManagementActiveTab}
                                items={[
                                    {
                                        key: 'summary',
                                        label: 'Resumen',
                                        children: (
                                            <Row gutter={[16, 16]}>
                                                <Col xs={24} lg={12}>
                                                    <Descriptions bordered size="small" column={1}>
                                                        <Descriptions.Item label="Organizacion">{selectedOrganizationManagement.organizationName}</Descriptions.Item>
                                                        <Descriptions.Item label="Email">{selectedOrganizationManagement.billingEmail || 'No registrado'}</Descriptions.Item>
                                                        <Descriptions.Item label="Telefono">
                                                            {selectedOrganizationManagement.organizationPhone || selectedOrganizationManagement.phone || 'No registrado'}
                                                        </Descriptions.Item>
                                                        <Descriptions.Item label="Contratos registrados">
                                                            {selectedOrganizationManagement.contracts.length}
                                                        </Descriptions.Item>
                                                    </Descriptions>
                                                </Col>
                                                <Col xs={24} lg={12}>
                                                    <Descriptions bordered size="small" column={1}>
                                                        <Descriptions.Item label="Plan vigente">
                                                            {selectedOrganizationManagement.hasContract ? selectedOrganizationManagement.nextContract.planName : 'Sin contrato'}
                                                        </Descriptions.Item>
                                                        <Descriptions.Item label="Estado">
                                                            <Tag color={getStatusColor(selectedOrganizationManagement.nextContract.status)}>
                                                                {selectedOrganizationManagement.nextContract.status}
                                                            </Tag>
                                                        </Descriptions.Item>
                                                        <Descriptions.Item label="Factura">
                                                            {selectedOrganizationManagement.hasContract && selectedOrganizationManagement.nextContract.lastSalesDocumentStatus
                                                                ? <Tag color={getStatusColor(selectedOrganizationManagement.nextContract.lastSalesDocumentStatus)}>{selectedOrganizationManagement.nextContract.lastSalesDocumentStatus}</Tag>
                                                                : <Tag>{selectedOrganizationManagement.hasContract ? 'Sin factura' : 'No aplica'}</Tag>}
                                                        </Descriptions.Item>
                                                        <Descriptions.Item label="Token PayPhone">
                                                            {renderCardTokenStatus(activeCardToken?.status)}
                                                        </Descriptions.Item>
                                                    </Descriptions>
                                                </Col>
                                            </Row>
                                        )
                                    },
                                    {
                                        key: 'current-contract',
                                        label: 'Contrato vigente',
                                        children: (
                                            <OrganizationContractManagement
                                                organizationId={selectedOrganizationManagement.organizationId}
                                                organizationName={selectedOrganizationManagement.organizationName}
                                            />
                                        )
                                    },
                                    {
                                        key: 'contracts',
                                        label: `Contratos (${selectedOrganizationManagement.contracts.length})`,
                                        children: (
                                            <Space direction="vertical" size={18} style={{ width: '100%' }}>
                                                <Table
                                                    rowKey="contractId"
                                                    columns={organizationContractColumns}
                                                    dataSource={selectedOrganizationManagement.contracts}
                                                    pagination={{ pageSize: 8 }}
                                                    scroll={{ x: 820 }}
                                                />
                                                <Divider orientation="left">Pagos PayPhone</Divider>
                                                <Table
                                                    rowKey="id"
                                                    dataSource={managementPaymentRequests}
                                                    pagination={{ pageSize: 5 }}
                                                    scroll={{ x: 900 }}
                                                    locale={{ emptyText: 'La organizacion no tiene pagos PayPhone registrados.' }}
                                                    columns={[
                                                        {
                                                            title: 'Contrato',
                                                            key: 'contract',
                                                            render: (_: unknown, row: PaymentRequest) => (
                                                                <Space direction="vertical" size={0}>
                                                                    <Text>{row.planName}</Text>
                                                                    <Text type="secondary">Contrato #{row.generatedContractNumber || row.currentContractNumber || 'No generado'}</Text>
                                                                </Space>
                                                            )
                                                        },
                                                        {
                                                            title: 'Pago',
                                                            key: 'payment',
                                                            render: (_: unknown, row: PaymentRequest) => (
                                                                <Space direction="vertical" size={0}>
                                                                    <Tag color={getStatusColor(row.status)}>{row.status}</Tag>
                                                                    <Text type="secondary">{formatUtcDate(row.paidAt || row.createdAt)}</Text>
                                                                </Space>
                                                            )
                                                        },
                                                        {
                                                            title: 'Transaccion',
                                                            key: 'transaction',
                                                            render: (_: unknown, row: PaymentRequest) => (
                                                                <Space direction="vertical" size={0}>
                                                                    <Text>{row.providerTransactionId || 'No registrada'}</Text>
                                                                    <Text type="secondary">{row.authorizationCode || row.clientTransactionId || 'Sin referencia'}</Text>
                                                                </Space>
                                                            )
                                                        },
                                                        {
                                                            title: 'Total',
                                                            dataIndex: 'total',
                                                            key: 'total',
                                                            align: 'right' as const,
                                                            render: (value: number, row: PaymentRequest) => money(value, row.currency)
                                                        },
                                                        {
                                                            title: 'Factura',
                                                            key: 'invoice',
                                                            render: (_: unknown, row: PaymentRequest) => <Tag color={getStatusColor(row.invoiceStatus)}>{row.invoiceStatus}</Tag>
                                                        }
                                                    ]}
                                                />
                                            </Space>
                                        )
                                    },
                                    {
                                        key: 'legal',
                                        label: `Legales (${managementPendingLegalDocuments.length})`,
                                        children: (
                                            <Space direction="vertical" size={14} style={{ width: '100%' }}>
                                                <Row gutter={[12, 12]}>
                                                    <Col xs={24} md={12}>
                                                        <Alert type={managementPendingLegalDocuments.length > 0 ? 'warning' : 'success'} showIcon message={`${managementPendingLegalDocuments.length} documento(s) pendiente(s)`} />
                                                    </Col>
                                                    <Col xs={24} md={12}>
                                                        <Alert type="info" showIcon message={`${managementAcceptedLegalDocuments.length} documento(s) vigente(s) aceptado(s)`} />
                                                    </Col>
                                                </Row>
                                                <Tabs
                                                    items={[
                                                        {
                                                            key: 'pending',
                                                            label: `Pendientes (${managementPendingLegalDocuments.length})`,
                                                            children: (
                                                                <Table
                                                                    rowKey={(row) => row.id || `${row.code}-${row.version}`}
                                                                    dataSource={managementPendingLegalDocuments}
                                                                    pagination={{ pageSize: 6 }}
                                                                    scroll={{ x: 780 }}
                                                                    locale={{ emptyText: 'La organizacion no tiene documentos legales vigentes pendientes.' }}
                                                                    columns={[
                                                                        {
                                                                            title: 'Documento',
                                                                            dataIndex: 'name',
                                                                            key: 'name',
                                                                            render: (value: string, row: LegalDocument) => (
                                                                                <Space direction="vertical" size={0}>
                                                                                    <Text strong>{value}</Text>
                                                                                    <Text type="secondary">{row.code} - version {row.version}</Text>
                                                                                    {row.description && <Text type="secondary">{row.description}</Text>}
                                                                                </Space>
                                                                            )
                                                                        },
                                                                        {
                                                                            title: 'Tipo',
                                                                            dataIndex: 'optional',
                                                                            key: 'optional',
                                                                            render: (value: boolean) => value ? 'Opcional' : 'Obligatorio'
                                                                        },
                                                                        {
                                                                            title: 'Estado',
                                                                            dataIndex: 'status',
                                                                            key: 'status',
                                                                            render: (value: string) => <Tag color={getStatusColor(value)}>{value}</Tag>
                                                                        },
                                                                        {
                                                                            title: '',
                                                                            key: 'actions',
                                                                            align: 'right' as const,
                                                                            render: (_: unknown, row: LegalDocument) => (
                                                                                <Button icon={<EyeOutlined />} href={row.url} target="_blank" rel="noreferrer" disabled={!row.url}>
                                                                                    Ver URL
                                                                                </Button>
                                                                            )
                                                                        }
                                                                    ]}
                                                                />
                                                            )
                                                        },
                                                        {
                                                            key: 'accepted',
                                                            label: `Aceptados (${managementAcceptedLegalDocuments.length})`,
                                                            children: (
                                                                <Table
                                                                    rowKey="id"
                                                                    dataSource={managementAcceptedLegalDocuments}
                                                                    pagination={{ pageSize: 6 }}
                                                                    scroll={{ x: 780 }}
                                                                    locale={{ emptyText: 'La organizacion no tiene documentos legales vigentes aceptados.' }}
                                                                    columns={[
                                                                        {
                                                                            title: 'Documento',
                                                                            dataIndex: 'name',
                                                                            key: 'name',
                                                                            render: (value: string, row: OrganizationLegalDocument) => (
                                                                                <Space direction="vertical" size={0}>
                                                                                    <Text strong>{value}</Text>
                                                                                    <Text type="secondary">{row.code} - version {row.version}</Text>
                                                                                    {row.description && <Text type="secondary">{row.description}</Text>}
                                                                                </Space>
                                                                            )
                                                                        },
                                                                        {
                                                                            title: 'Aceptado',
                                                                            dataIndex: 'acceptedAt',
                                                                            key: 'acceptedAt',
                                                                            render: (value: string) => formatUtcDate(value)
                                                                        },
                                                                        {
                                                                            title: 'Tipo',
                                                                            dataIndex: 'optional',
                                                                            key: 'optional',
                                                                            render: (value: boolean) => value ? 'Opcional' : 'Obligatorio'
                                                                        },
                                                                        {
                                                                            title: '',
                                                                            key: 'actions',
                                                                            align: 'right' as const,
                                                                            render: (_: unknown, row: OrganizationLegalDocument) => (
                                                                                <Button icon={<EyeOutlined />} href={row.url} target="_blank" rel="noreferrer" disabled={!row.url}>
                                                                                    Ver URL
                                                                                </Button>
                                                                            )
                                                                        }
                                                                    ]}
                                                                />
                                                            )
                                                        }
                                                    ]}
                                                />
                                            </Space>
                                        )
                                    },
                                    {
                                        key: 'payment',
                                        label: 'Pago y factura',
                                        children: (
                                            <Tabs
                                                defaultActiveKey="manual"
                                                items={[
                                                    {
                                                        key: 'manual',
                                                        label: 'Manual',
                                                        children: (
                                                            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                                                                <Alert
                                                                    type="info"
                                                                    showIcon
                                                                    message="Creacion y facturacion manual"
                                                                    description="Use esta opcion para crear un contrato gratuito o registrar una factura cuando el cliente paga fuera de PayPhone, por ejemplo en efectivo o transferencia."
                                                                />
                                                                {renderManagementBillingOptions('manual')}
                                                                {!selectedOrganizationManagement.hasContract && (
                                                                    <Alert
                                                                        type="warning"
                                                                        showIcon
                                                                        message="La organizacion todavia no tiene contrato"
                                                                        description="Puede crear el contrato manualmente con el plan seleccionado. La validacion de tarjeta solo aplica para cobros automaticos con PayPhone."
                                                                    />
                                                                )}
                                                                <Button
                                                                    type="primary"
                                                                    icon={<FileSearchOutlined />}
                                                                    onClick={handleContinueManagementManualBilling}
                                                                    disabled={!managementPlanId}
                                                                >
                                                                    {!selectedOrganizationManagement.hasContract
                                                                        ? 'Generar contrato manual'
                                                                        : isManagementFreePlan
                                                                            ? 'Generar contrato gratis'
                                                                            : 'Continuar manual'}
                                                                </Button>
                                                            </Space>
                                                        )
                                                    },
                                                    {
                                                        key: 'automatic',
                                                        label: 'Automatico PayPhone',
                                                        children: (
                                                            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                                                                <Alert
                                                                    type="info"
                                                                    showIcon
                                                                    message="Cobro automatico con PayPhone"
                                                                    description="Use esta opcion para cobrar con la tarjeta tokenizada. Si no existe una tarjeta activa, primero debe validarla en la pestana Tarjetas."
                                                                />
                                                                {renderManagementBillingOptions('automatic')}
                                                                <Descriptions bordered size="small" column={1}>
                                                                    <Descriptions.Item label="Token de tarjeta">
                                                                        {renderCardTokenStatus(activeCardToken?.status)}
                                                                    </Descriptions.Item>
                                                                    <Descriptions.Item label="Tarjeta">
                                                                        {activeCardToken?.status === 'ACTIVO'
                                                                            ? formatCardTokenLabel(activeCardToken)
                                                                            : 'No registrada'}
                                                                    </Descriptions.Item>
                                                                    <Descriptions.Item label="Expiracion">
                                                                        {formatCardExpiry(activeCardToken)}
                                                                    </Descriptions.Item>
                                                                </Descriptions>
                                                                <Space wrap>
                                                                    <Button
                                                                        icon={<CreditCardOutlined />}
                                                                        onClick={() => setManagementActiveTab('cards')}
                                                                    >
                                                                        {activeCardToken?.status === 'ACTIVO' ? 'Cambiar tarjeta' : 'Validar tarjeta'}
                                                                    </Button>
                                                                    <Button
                                                                        type="primary"
                                                                        icon={<CreditCardOutlined />}
                                                                        onClick={handleChargeManagementWithPayPhone}
                                                                        disabled={!selectedOrganizationManagement.hasContract || !managementPlanId || isManagementFreePlan}
                                                                    >
                                                                        Cobrar y generar contrato
                                                                    </Button>
                                                                </Space>
                                                            </Space>
                                                        )
                                                    }
                                                ]}
                                            />
                                        )
                                    },
                                    {
                                        key: 'cards',
                                        label: 'Tarjetas',
                                        children: (
                                            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                                                <Alert
                                                    type={activeCardToken?.status === 'ACTIVO' ? 'success' : 'warning'}
                                                    showIcon
                                                    message={activeCardToken?.status === 'ACTIVO'
                                                        ? 'Tarjeta validada'
                                                        : 'Tarjeta pendiente de validacion'}
                                                    description={activeCardToken?.status === 'ACTIVO'
                                                        ? 'Desde aqui puede cambiar la tarjeta, revalidarla o desactivarla para cobros automaticos.'
                                                        : 'Genere el link de PayPhone para validar la tarjeta y guardar el token de cobro automatico.'}
                                                />
                                                <Descriptions bordered size="small" column={1}>
                                                    <Descriptions.Item label="Token de tarjeta">
                                                        {renderCardTokenStatus(activeCardToken?.status)}
                                                    </Descriptions.Item>
                                                    <Descriptions.Item label="Tarjeta">
                                                        {formatCardTokenLabel(activeCardToken)}
                                                    </Descriptions.Item>
                                                    <Descriptions.Item label="Titular">
                                                        {activeCardToken?.cardHolderName || 'No registrado'}
                                                    </Descriptions.Item>
                                                    <Descriptions.Item label="Documento">
                                                        {activeCardToken?.document || 'No registrado'}
                                                    </Descriptions.Item>
                                                    <Descriptions.Item label="Email">
                                                        {activeCardToken?.email || 'No registrado'}
                                                    </Descriptions.Item>
                                                    <Descriptions.Item label="Telefono">
                                                        {activeCardToken?.phone || 'No registrado'}
                                                    </Descriptions.Item>
                                                    <Descriptions.Item label="Ultima validacion">
                                                        {activeCardToken?.validationPaidAt
                                                            ? `${money(Number(activeCardToken.validationAmount || 0), activeCardToken.validationCurrency || 'USD')} - ${formatUtcDate(activeCardToken.validationPaidAt)}`
                                                            : 'Sin validacion'}
                                                    </Descriptions.Item>
                                                    <Descriptions.Item label="Reverso">
                                                        {renderReverseStatus(activeCardToken?.reverseStatus)}
                                                    </Descriptions.Item>
                                                </Descriptions>
                                                {cardTokens.length > 0 && (
                                                    <>
                                                        <Divider orientation="left">Historial de tarjetas</Divider>
                                                        <Table
                                                            size="small"
                                                            rowKey={(record, index) => record.id || record.validationClientTransactionId || `${record.provider || 'token'}-${index}`}
                                                            dataSource={cardTokens}
                                                            pagination={{ pageSize: 5 }}
                                                            columns={[
                                                                {
                                                                    title: 'Estado',
                                                                    dataIndex: 'status',
                                                                    render: (value: string) => renderCardTokenStatus(value)
                                                                },
                                                                {
                                                                    title: 'Tarjeta',
                                                                    render: (_value: unknown, record: OrganizationCardToken) => formatCardTokenLabel(record)
                                                                },
                                                                {
                                                                    title: 'Titular',
                                                                    dataIndex: 'cardHolderName',
                                                                    render: (value: string) => value || 'No registrado'
                                                                },
                                                                {
                                                                    title: 'Validacion',
                                                                    render: (_value: unknown, record: OrganizationCardToken) => record.validationPaidAt
                                                                        ? `${money(Number(record.validationAmount || 0), record.validationCurrency || 'USD')} - ${formatUtcDate(record.validationPaidAt)}`
                                                                        : 'Sin validacion'
                                                                },
                                                                {
                                                                    title: 'Reverso',
                                                                    dataIndex: 'reverseStatus',
                                                                    render: (value: string) => renderReverseStatus(value)
                                                                }
                                                            ]}
                                                        />
                                                    </>
                                                )}
                                                <Space wrap>
                                                    {activeCardToken?.status === 'ACTIVO' && (
                                                        <Button
                                                            danger
                                                            icon={<StopOutlined />}
                                                            onClick={handleDisableActiveCardToken}
                                                        >
                                                            Desactivar tarjeta
                                                        </Button>
                                                    )}
                                                    <Button
                                                        type="primary"
                                                        icon={<CreditCardOutlined />}
                                                        onClick={async () => {
                                                            const contract = selectedOrganizationManagement.nextContract;
                                                            handleCloseOrganizationManagement();
                                                            await handleOpenPayPhonePayment(contract);
                                                        }}
                                                    >
                                                        {activeCardToken?.status === 'ACTIVO' ? 'Cambiar tarjeta' : 'Validar tarjeta'}
                                                    </Button>
                                                </Space>
                                            </Space>
                                        )
                                    }
                                ]}
                            />
                        </Space>
                    )}
                </Drawer>

                <Modal
                    open={!!selectedOrganizationContracts}
                    title={selectedOrganizationContracts ? `Contratos - ${selectedOrganizationContracts.organizationName}` : 'Contratos'}
                    width={1050}
                    footer={[
                        <Button key="close" onClick={() => setSelectedOrganizationContracts(null)}>
                            Cerrar
                        </Button>
                    ]}
                    onCancel={() => setSelectedOrganizationContracts(null)}
                    destroyOnHidden
                >
                    <Table
                        rowKey="contractId"
                        columns={organizationContractColumns}
                        dataSource={selectedOrganizationContracts?.contracts || []}
                        pagination={{ pageSize: 6 }}
                        scroll={{ x: 820 }}
                    />
                </Modal>

                <Modal
                    open={isRegisterModalOpen}
                    title="Registrar empresa"
                    width={980}
                    okText={registerRequiresCardValidation ? 'Crear y generar link PayPhone' : 'Crear contrato gratis'}
                    cancelText="Cerrar"
                    onOk={handleOnboardingRegister}
                    onCancel={() => {
                        setIsRegisterModalOpen(false);
                        setRegisterResult(null);
                        registerForm.resetFields();
                    }}
                    destroyOnHidden
                >
                    <Space direction="vertical" size={16} style={{ width: '100%' }}>
                        <Alert
                            type={registerRequiresCardValidation ? 'info' : 'success'}
                            showIcon
                            message={registerRequiresCardValidation
                                ? 'El plan pagado no crea contrato hasta que PayPhone valide la tarjeta y devuelva el token.'
                                : 'El plan gratis crea el contrato directamente, sin datos de factura ni PayPhone.'}
                        />

                        <Form form={registerForm} layout="vertical" onValuesChange={syncRegisterBillingData}>
                            <Form.Item name="isTestOrganization" valuePropName="checked" style={{ marginBottom: 0 }}>
                                <Checkbox onChange={(event) => handleTestOrganizationChange(event.target.checked)}>
                                    Organizacion de prueba
                                </Checkbox>
                            </Form.Item>

                            <Divider orientation="left">
                                {registerIsClinic ? 'Empresa y usuario administrador' : 'Empresa y usuario principal'}
                            </Divider>
                            <Row gutter={[16, 0]}>
                                <Col xs={24} md={6}>
                                    <Form.Item
                                        label="Primer nombre"
                                        name="firstName"
                                        rules={[{ required: true, message: 'Ingrese el primer nombre' }]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={6}>
                                    <Form.Item label="Segundo nombre" name="secondName">
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={6}>
                                    <Form.Item
                                        label="Primer apellido"
                                        name="lastName"
                                        rules={[{ required: true, message: 'Ingrese el primer apellido' }]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={6}>
                                    <Form.Item label="Segundo apellido" name="secondLastName">
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Form.Item
                                        label="Identificacion"
                                        name="licenseNumber"
                                        rules={[{ required: true, message: 'Ingrese la identificacion' }]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Form.Item
                                        label="Email"
                                        name="email"
                                        rules={[
                                            { required: true, message: 'Ingrese el email' },
                                            { type: 'email', message: 'Ingrese un email valido' }
                                        ]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Form.Item
                                        label="Telefono"
                                        name="phone"
                                        rules={[{ required: true, message: 'Ingrese el telefono' }]}
                                    >
                                        <Input placeholder="+593..." />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Contrasena"
                                        name="password"
                                        rules={[{ required: true, message: 'Ingrese una contrasena temporal' }]}
                                    >
                                        <Input.Password />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Divider orientation="left">Plan y configuracion inicial</Divider>
                            <Row gutter={[16, 0]}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Plan"
                                        name="planId"
                                        rules={[{ required: true, message: 'Seleccione el plan' }]}
                                    >
                                        <Select
                                            showSearch
                                            optionFilterProp="label"
                                            onChange={handleRegisterPlanChange}
                                            options={plans.map(plan => ({
                                                value: plan.id || '',
                                                label: `${plan.name} - ${money(Number(plan.price || 0))}`
                                            }))}
                                        />
                                    </Form.Item>
                                </Col>
                                {!registerIsClinic && (
                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Especialidad"
                                            name="specialtyId"
                                            rules={[{ required: true, message: 'Seleccione la especialidad' }]}
                                        >
                                            <Select
                                                showSearch
                                                optionFilterProp="label"
                                                onChange={handleRegisterSpecialityChange}
                                                options={specialities.map(speciality => ({
                                                    value: speciality.id || '',
                                                    label: speciality.name
                                                }))}
                                            />
                                        </Form.Item>
                                    </Col>
                                )}
                                {!registerIsClinic && (
                                    <Col xs={24}>
                                        <Form.Item
                                            label="Secciones de ficha"
                                            name="medicalRecordSectionIds"
                                            rules={[{ required: true, message: 'Seleccione al menos una seccion' }]}
                                        >
                                            <Select
                                                mode="multiple"
                                                optionFilterProp="label"
                                                options={medicalRecordSections.map(section => ({
                                                    value: section.id || '',
                                                    label: section.name
                                                }))}
                                            />
                                        </Form.Item>
                                    </Col>
                                )}
                                {registerIsClinic && (
                                    <>
                                        <Col xs={24}>
                                            <Form.Item
                                                label="Especialidades de la clinica"
                                                name="clinicSpecialtyIds"
                                                rules={[{ required: true, message: 'Seleccione al menos una especialidad' }]}
                                            >
                                                <Select
                                                    mode="multiple"
                                                    showSearch
                                                    optionFilterProp="label"
                                                    maxCount={registerMaxSpecialities ?? undefined}
                                                    options={specialities.map(speciality => ({
                                                        value: speciality.id || '',
                                                        label: speciality.name
                                                    }))}
                                                />
                                            </Form.Item>
                                        </Col>
                                        {registerMaxSpecialities !== null && (
                                            <Col xs={24}>
                                                <Text type="secondary">
                                                    Maximo de especialidades del plan: {registerMaxSpecialities}
                                                </Text>
                                            </Col>
                                        )}
                                        <Col xs={24}>
                                            <Divider orientation="left">Centros medicos</Divider>
                                            <Form.List name="clinicCenters">
                                                {(fields, { add, remove }) => (
                                                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                                        {fields.map((field, index) => (
                                                            <Card key={field.key} size="small" title={`Centro ${index + 1}`}>
                                                                <Row gutter={[12, 0]}>
                                                                    <Col xs={24} md={8}>
                                                                        <Form.Item
                                                                            {...field}
                                                                            label="Nombre"
                                                                            name={[field.name, 'name']}
                                                                            rules={[{ required: true, message: 'Ingrese el nombre del centro' }]}
                                                                        >
                                                                            <Input />
                                                                        </Form.Item>
                                                                    </Col>
                                                                    <Col xs={24} md={8}>
                                                                        <Form.Item
                                                                            {...field}
                                                                            label="Direccion"
                                                                            name={[field.name, 'address']}
                                                                            rules={[{ required: true, message: 'Ingrese la direccion del centro' }]}
                                                                        >
                                                                            <Input />
                                                                        </Form.Item>
                                                                    </Col>
                                                                    <Col xs={24} md={4}>
                                                                        <Form.Item {...field} label="Latitud" name={[field.name, 'latitud']}>
                                                                            <Input />
                                                                        </Form.Item>
                                                                    </Col>
                                                                    <Col xs={24} md={4}>
                                                                        <Form.Item {...field} label="Longitud" name={[field.name, 'longitude']}>
                                                                            <Input />
                                                                        </Form.Item>
                                                                    </Col>
                                                                    <Col xs={24}>
                                                                        <Button danger icon={<StopOutlined />} onClick={() => remove(field.name)}>
                                                                            Quitar centro
                                                                        </Button>
                                                                    </Col>
                                                                </Row>
                                                            </Card>
                                                        ))}
                                                        <Button
                                                            type="dashed"
                                                            icon={<PlusOutlined />}
                                                            disabled={registerMaxCenters !== null && registerMaxCenters > 0 && fields.length >= registerMaxCenters}
                                                            onClick={() => add({ name: '', address: '', latitud: '', longitude: '' })}
                                                        >
                                                            Agregar centro
                                                        </Button>
                                                        {registerMaxCenters !== null && (
                                                <Text type="secondary">Maximo de centros del plan: {registerMaxCenters}</Text>
                                                        )}
                                                    </Space>
                                                )}
                                            </Form.List>
                                        </Col>
                                        <Col xs={24}>
                                            <Divider orientation="left">Medicos de la clinica</Divider>
                                            {registerDoctorCapacity !== null && registerDoctorCapacity > 0 && (
                                                <Alert
                                                    type="info"
                                                    showIcon
                                                    message={`Maximo ${registerDoctorCapacity} medicos para el rol configurado`}
                                                    style={{ marginBottom: 12 }}
                                                />
                                            )}
                                            <Form.List name="clinicDoctors">
                                                {(fields, { add, remove }) => (
                                                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                                        {fields.map((field, index) => (
                                                            <Card key={field.key} size="small" title={`Medico ${index + 1}`}>
                                                                <Row gutter={[12, 0]}>
                                                                    <Col xs={24} md={6}>
                                                                        <Form.Item {...field} label="Primer nombre" name={[field.name, 'firstName']} rules={[{ required: true, message: 'Ingrese el nombre' }]}>
                                                                            <Input />
                                                                        </Form.Item>
                                                                    </Col>
                                                                    <Col xs={24} md={6}>
                                                                        <Form.Item {...field} label="Primer apellido" name={[field.name, 'lastName']} rules={[{ required: true, message: 'Ingrese el apellido' }]}>
                                                                            <Input />
                                                                        </Form.Item>
                                                                    </Col>
                                                                    <Col xs={24} md={6}>
                                                                        <Form.Item {...field} label="Email" name={[field.name, 'email']} rules={[{ required: true, message: 'Ingrese el email' }, { type: 'email', message: 'Ingrese un email valido' }]}>
                                                                            <Input />
                                                                        </Form.Item>
                                                                    </Col>
                                                                    <Col xs={24} md={6}>
                                                                        <Form.Item {...field} label="Telefono" name={[field.name, 'phone']} rules={[{ required: true, message: 'Ingrese el telefono' }]}>
                                                                            <Input />
                                                                        </Form.Item>
                                                                    </Col>
                                                                    <Col xs={24} md={8}>
                                                                        <Form.Item {...field} label="Licencia" name={[field.name, 'licenseNumber']} rules={[{ required: true, message: 'Ingrese la licencia' }]}>
                                                                            <Input />
                                                                        </Form.Item>
                                                                    </Col>
                                                                    <Col xs={24} md={8}>
                                                                        <Form.Item {...field} label="Especialidad" name={[field.name, 'specialtyId']} rules={[{ required: true, message: 'Seleccione la especialidad' }]}>
                                                                            <Select
                                                                                showSearch
                                                                                optionFilterProp="label"
                                                                                options={(registerClinicSpecialities.length > 0 ? registerClinicSpecialities : specialities).map(speciality => ({
                                                                                    value: speciality.id || '',
                                                                                    label: speciality.name
                                                                                }))}
                                                                            />
                                                                        </Form.Item>
                                                                    </Col>
                                                                    <Col xs={24} md={8}>
                                                                        <Form.Item {...field} label="Centro" name={[field.name, 'centerIndex']} rules={[{ required: true, message: 'Seleccione el centro' }]}>
                                                                            <Select
                                                                                options={registerClinicCenters.map((center: any, centerIndex: number) => ({
                                                                                    value: centerIndex,
                                                                                    label: center?.name || `Centro ${centerIndex + 1}`
                                                                                }))}
                                                                            />
                                                                        </Form.Item>
                                                                    </Col>
                                                                    <Col xs={24} md={8}>
                                                                        <Form.Item {...field} label="Días de atención" name={[field.name, 'weekDayIds']} rules={[{ required: true, message: 'Seleccione los días' }]}>
                                                                            <Select mode="multiple" options={defaultWeekDayIds.map((id, dayIndex) => ({ value: id, label: defaultWeekDayLabels[dayIndex] }))} />
                                                                        </Form.Item>
                                                                    </Col>
                                                                    <Col xs={24} md={5}>
                                                                        <Form.Item {...field} label="Inicio" name={[field.name, 'startTime']} rules={[{ required: true, message: 'Ingrese la hora' }]}>
                                                                            <Input />
                                                                        </Form.Item>
                                                                    </Col>
                                                                    <Col xs={24} md={5}>
                                                                        <Form.Item {...field} label="Fin" name={[field.name, 'endTime']} rules={[{ required: true, message: 'Ingrese la hora' }]}>
                                                                            <Input />
                                                                        </Form.Item>
                                                                    </Col>
                                                                    <Col xs={24} md={4}>
                                                                        <Form.Item {...field} label="Minutos" name={[field.name, 'slotMinutes']} rules={[{ required: true, message: 'Ingrese los minutos' }]}>
                                                                            <InputNumber min={5} max={180} style={{ width: '100%' }} />
                                                                        </Form.Item>
                                                                    </Col>
                                                                    <Col xs={24}>
                                                                        <Button danger icon={<StopOutlined />} onClick={() => remove(field.name)}>
                                                                            Quitar medico
                                                                        </Button>
                                                                    </Col>
                                                                </Row>
                                                            </Card>
                                                        ))}
                                                        <Button
                                                            type="dashed"
                                                            icon={<PlusOutlined />}
                                                            onClick={() => add({
                                                                specialtyId: registerClinicSpecialityIds[0],
                                                                centerIndex: 0,
                                                                weekDayIds: defaultWeekDayIds,
                                                                startTime: '08:00',
                                                                endTime: '17:00',
                                                                slotMinutes: 30
                                                            })}
                                                        >
                                                            Agregar medico
                                                        </Button>
                                                    </Space>
                                                )}
                                            </Form.List>
                                        </Col>
                                        <Col xs={24}>
                                            <Divider orientation="left">Enfermeros de la clínica</Divider>
                                            {registerNurseCapacity !== null && registerNurseCapacity > 0 && (
                                                <Alert
                                                    type="info"
                                                    showIcon
                                                    message={`Máximo ${registerNurseCapacity} enfermeros según el plan`}
                                                    style={{ marginBottom: 12 }}
                                                />
                                            )}
                                            <Form.List name="clinicNurses">
                                                {(fields, { add, remove }) => (
                                                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                                        {fields.map((field, index) => (
                                                            <Card key={field.key} size="small" title={`Enfermero/a ${index + 1}`}>
                                                                <Row gutter={[12, 0]}>
                                                                    <Col xs={24} md={6}>
                                                                        <Form.Item {...field} label="Primer nombre" name={[field.name, 'firstName']} rules={[{ required: true, message: 'Ingrese el nombre' }]}>
                                                                            <Input />
                                                                        </Form.Item>
                                                                    </Col>
                                                                    <Col xs={24} md={6}>
                                                                        <Form.Item {...field} label="Primer apellido" name={[field.name, 'lastName']} rules={[{ required: true, message: 'Ingrese el apellido' }]}>
                                                                            <Input />
                                                                        </Form.Item>
                                                                    </Col>
                                                                    <Col xs={24} md={6}>
                                                                        <Form.Item {...field} label="Email" name={[field.name, 'email']} rules={[{ required: true, message: 'Ingrese el email' }, { type: 'email', message: 'Ingrese un email válido' }]}>
                                                                            <Input />
                                                                        </Form.Item>
                                                                    </Col>
                                                                    <Col xs={24} md={6}>
                                                                        <Form.Item {...field} label="Teléfono" name={[field.name, 'phone']}>
                                                                            <Input />
                                                                        </Form.Item>
                                                                    </Col>
                                                                    <Col xs={24}>
                                                                        <Button danger icon={<StopOutlined />} onClick={() => remove(field.name)}>
                                                                            Quitar enfermero/a
                                                                        </Button>
                                                                    </Col>
                                                                </Row>
                                                            </Card>
                                                        ))}
                                                        <Button
                                                            type="dashed"
                                                            icon={<PlusOutlined />}
                                                            disabled={registerNurseCapacity !== null && registerNurseCapacity > 0 && fields.length >= registerNurseCapacity}
                                                            onClick={() => add({ firstName: '', lastName: '', email: '', phone: '' })}
                                                        >
                                                            Agregar enfermero/a
                                                        </Button>
                                                    </Space>
                                                )}
                                            </Form.List>
                                        </Col>
                                    </>
                                )}
                                <Col xs={24}>
                                    <Form.Item
                                        label="Documentos legales aceptados"
                                        name="legalDocuments"
                                        rules={[{ required: true, message: 'Seleccione los documentos aceptados' }]}
                                    >
                                        <Select
                                            mode="multiple"
                                            optionFilterProp="label"
                                            options={onboardingLegalDocuments.map(document => ({
                                                value: document.id || '',
                                                label: `${document.name} - ${document.version}${document.optional ? ' (opcional)' : ''}`
                                            }))}
                                        />
                                    </Form.Item>
                                </Col>
                                {!registerIsClinic && (
                                    <>
                                        <Col xs={24} md={8}>
                                            <Form.Item label="Inicio" name="startTime">
                                                <Input />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={8}>
                                            <Form.Item label="Fin" name="endTime">
                                                <Input />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={8}>
                                            <Form.Item label="Minutos por cita" name="slotMinutes">
                                                <InputNumber min={5} max={180} style={{ width: '100%' }} />
                                            </Form.Item>
                                        </Col>
                                    </>
                                )}
                            </Row>

                            <Divider orientation="left">Datos de facturacion preferidos</Divider>
                            <Row gutter={[16, 0]}>
                                <Col xs={24} md={8}>
                                    <Form.Item
                                        label="RUC / identificacion"
                                        name="clientRuc"
                                        rules={[{ required: true, message: 'Ingrese el RUC o identificacion' }]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={16}>
                                    <Form.Item
                                        label="Nombre o razon social"
                                        name="clientName"
                                        rules={[{ required: true, message: 'Ingrese el nombre de facturacion' }]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Email facturacion"
                                        name="clientEmail"
                                        rules={[
                                            { required: true, message: 'Ingrese el email de facturacion' },
                                            { type: 'email', message: 'Ingrese un email valido' }
                                        ]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Telefono facturacion"
                                        name="clientPhone"
                                        rules={[{ required: true, message: 'Ingrese el telefono de facturacion' }]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col xs={24}>
                                    <Form.Item
                                        label="Direccion"
                                        name="clientAddress"
                                        rules={[{ required: true, message: 'Ingrese la direccion' }]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form>

                        {registerResult && (
                            <Alert
                                type={registerResult.requiresCardValidation ? 'warning' : 'success'}
                                showIcon
                                message={registerResult.requiresCardValidation
                                    ? 'Organizacion creada. Falta validar tarjeta para activar el contrato.'
                                    : 'Organizacion creada con contrato gratis activo.'}
                                description={registerResult.requiresCardValidation && registerResult.payPhone ? (
                                    <Space direction="vertical" size={8}>
                                        <Text>Transaccion: {registerResult.payPhone.clientTransactionId}</Text>
                                        <Space wrap>
                                            <Button type="primary" icon={<CreditCardOutlined />} onClick={openRegisterPayPhoneLink}>
                                                Abrir PayPhone
                                            </Button>
                                            <Text copyable={{ text: registerResult.payPhone.payWithCard || registerResult.payPhone.payWithPayPhone || '' }}>
                                                Copiar link de pago
                                            </Text>
                                        </Space>
                                    </Space>
                                ) : undefined}
                            />
                        )}
                    </Space>
                </Modal>

                <Modal
                    open={!!onboardingPayPhoneUrl}
                    title="Validacion de tarjeta PayPhone"
                    width="92vw"
                    footer={[
                        <Button key="external" icon={<CreditCardOutlined />} onClick={() => window.open(onboardingPayPhoneUrl, '_blank')}>
                            Abrir en otra pestana
                        </Button>,
                        <Button key="close" onClick={() => setOnboardingPayPhoneUrl('')}>
                            Cerrar
                        </Button>
                    ]}
                    onCancel={() => setOnboardingPayPhoneUrl('')}
                    destroyOnHidden
                >
                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                        <Alert
                            type="info"
                            showIcon
                            message={`Complete el pago de ${money(registerPayPhoneInitialAmount || 1)} para guardar el token de la tarjeta.`}
                            description="Cuando PayPhone confirme la transaccion, regresara a la pantalla de confirmacion del portal."
                        />
                        <iframe
                            title="PayPhone validacion de tarjeta"
                            src={onboardingPayPhoneUrl}
                            style={{
                                width: '100%',
                                height: '72vh',
                                border: '1px solid #d9d9d9',
                                borderRadius: 8,
                                background: '#fff'
                            }}
                        />
                    </Space>
                </Modal>

                <Modal
                    open={!!paymentActionRow}
                    title={`Pago y facturacion${paymentActionRow ? ` - ${paymentActionRow.organizationName}` : ''}`}
                    width={760}
                    footer={null}
                    onCancel={() => {
                        setPaymentActionRow(null);
                        setActiveCardToken(null);
                        setCardTokens([]);
                    }}
                    destroyOnHidden
                >
                    {paymentActionRow && (
                        <Tabs
                            defaultActiveKey={paymentActionRow.hasContract ? 'manual' : 'automatic'}
                            items={[
                                {
                                    key: 'manual',
                                    label: 'Manual',
                                    children: (
                                        <Space direction="vertical" size={16} style={{ width: '100%' }}>
                                            <Alert
                                                type="info"
                                                showIcon
                                                message="Creacion y facturacion manual"
                                                description="Use esta opcion cuando el cliente paga fuera de PayPhone, por ejemplo en efectivo, transferencia u otro medio acordado. Desde aqui se registra la factura o se genera el contrato gratuito cuando corresponda."
                                            />
                                            <Descriptions bordered size="small" column={1}>
                                                <Descriptions.Item label="Organizacion">{paymentActionRow.organizationName}</Descriptions.Item>
                                                <Descriptions.Item label="Contrato">
                                                    {paymentActionRow.hasContract
                                                        ? `#${paymentActionRow.nextContract.contractNumber}`
                                                        : 'Sin contrato generado'}
                                                </Descriptions.Item>
                                                <Descriptions.Item label="Plan actual">{paymentActionRow.nextContract.planName}</Descriptions.Item>
                                                <Descriptions.Item label="Valor">
                                                    {money(Number(paymentActionRow.nextContract.totalPrice || 0), paymentActionRow.nextContract.currency || 'USD')}
                                                </Descriptions.Item>
                                            </Descriptions>
                                            {!paymentActionRow.hasContract && (
                                                <Alert
                                                    type="warning"
                                                    showIcon
                                                    message="La organizacion todavia no tiene contrato"
                                                    description="Para el flujo manual no necesita validar tarjeta. Use el modulo de gestion de la organizacion para crear el contrato inicial con el plan seleccionado."
                                                />
                                            )}
                                            <Button
                                                type="primary"
                                                icon={<FileSearchOutlined />}
                                                onClick={handleContinueManualBilling}
                                                disabled={!paymentActionRow.hasContract}
                                            >
                                                Continuar manual
                                            </Button>
                                        </Space>
                                    )
                                },
                                {
                                    key: 'automatic',
                                    label: 'Automatico PayPhone',
                                    children: (
                                        <Space direction="vertical" size={16} style={{ width: '100%' }}>
                                            <Alert
                                                type={activeCardToken?.status === 'ACTIVO' ? 'success' : 'warning'}
                                                showIcon
                                                message={activeCardToken?.status === 'ACTIVO'
                                                    ? 'Tarjeta validada'
                                                    : 'Tarjeta pendiente de validacion'}
                                                description={activeCardToken?.status === 'ACTIVO'
                                                    ? 'Use esta opcion para cambiar la tarjeta, revalidarla o preparar cobros automaticos con PayPhone.'
                                                    : 'Use esta opcion para generar el link de PayPhone y guardar el token de la tarjeta. Esta validacion siempre cobra $1.00 y luego se reversa.'}
                                            />
                                            <Descriptions bordered size="small" column={1}>
                                                <Descriptions.Item label="Organizacion">{paymentActionRow.organizationName}</Descriptions.Item>
                                                <Descriptions.Item label="Plan">{paymentActionRow.nextContract.planName}</Descriptions.Item>
                                                <Descriptions.Item label="Token de tarjeta">
                                                    {renderCardTokenStatus(activeCardToken?.status)}
                                                </Descriptions.Item>
                                                <Descriptions.Item label="Tarjeta">
                                                    {formatCardTokenLabel(activeCardToken)}
                                                </Descriptions.Item>
                                                <Descriptions.Item label="Expiracion">
                                                    {formatCardExpiry(activeCardToken)}
                                                </Descriptions.Item>
                                                <Descriptions.Item label="Titular">
                                                    {activeCardToken?.cardHolderName || 'No registrado'}
                                                </Descriptions.Item>
                                                <Descriptions.Item label="Ultima validacion">
                                                    {activeCardToken?.validationPaidAt
                                                        ? `${money(Number(activeCardToken.validationAmount || 0), activeCardToken.validationCurrency || 'USD')} - ${formatUtcDate(activeCardToken.validationPaidAt)}`
                                                        : 'Sin validacion'}
                                                </Descriptions.Item>
                                                <Descriptions.Item label="Reverso">
                                                    {renderReverseStatus(activeCardToken?.reverseStatus)}
                                                </Descriptions.Item>
                                            </Descriptions>
                                            <Space wrap>
                                                <Button
                                                    type="primary"
                                                    icon={<CreditCardOutlined />}
                                                    onClick={handleContinueAutomaticBilling}
                                                >
                                                    {activeCardToken?.status === 'ACTIVO' ? 'Administrar tarjeta' : 'Validar tarjeta'}
                                                </Button>
                                            </Space>
                                        </Space>
                                    )
                                }
                            ]}
                        />
                    )}
                </Modal>

                <Modal
                    open={!!payPhoneContract}
                    title="Administrador de tarjeta PayPhone"
                    width={720}
                    footer={[
                        activeCardToken?.status === 'ACTIVO' ? (
                            <Button
                                key="disable"
                                danger
                                icon={<StopOutlined />}
                                onClick={handleDisableActiveCardToken}
                            >
                                Desactivar tarjeta
                            </Button>
                        ) : null,
                        <Button
                            key="generate"
                            type={payPhoneUrl ? 'default' : 'primary'}
                            icon={<CreditCardOutlined />}
                            onClick={handlePayPhonePayment}
                        >
                            {activeCardToken?.status === 'ACTIVO'
                                ? 'Cambiar tarjeta'
                                : payPhoneUrl
                                    ? 'Reintentar validacion'
                                    : 'Validar tarjeta'}
                        </Button>,
                        payPhoneUrl ? (
                            <Button
                                key="open"
                                type="primary"
                                icon={<CreditCardOutlined />}
                                onClick={() => window.open(payPhoneUrl, '_blank')}
                            >
                                Abrir PayPhone
                            </Button>
                        ) : null,
                        <Button
                            key="close"
                            onClick={() => {
                                setPayPhoneContract(null);
                                setPayPhoneClientData(null);
                                setPayPhoneUrl('');
                                setActiveCardToken(null);
                                setCardTokens([]);
                            }}
                        >
                            Cerrar
                        </Button>
                    ]}
                    onCancel={() => {
                        setPayPhoneContract(null);
                        setPayPhoneClientData(null);
                        setPayPhoneUrl('');
                        setActiveCardToken(null);
                        setCardTokens([]);
                    }}
                    destroyOnHidden
                >
                    {payPhoneContract && (
                        <Space direction="vertical" size={16} style={{ width: '100%' }}>
                            <Alert
                                type={activeCardToken?.status === 'ACTIVO' ? 'success' : 'warning'}
                                showIcon
                                message={activeCardToken?.status === 'ACTIVO'
                                    ? 'Tarjeta activa para cobros automaticos'
                                    : 'Tarjeta pendiente de validacion'}
                                description={activeCardToken?.status === 'ACTIVO'
                                    ? 'Desde aqui puede cambiar la tarjeta, revalidarla o desactivarla. Cada nueva validacion cobra $1.00 y PayPhone reversa el cargo.'
                                    : 'Genere el link de PayPhone para guardar el token de la tarjeta. Esta validacion siempre cobra $1.00 y luego se reversa, aunque no exista contrato.'}
                            />
                            {payPhoneUrl && (
                                <Alert
                                    type="info"
                                    showIcon
                                    message="Link de PayPhone generado"
                                    description="Abra el link en otra pestana. PayPhone no permite cargarse dentro de un iframe, por eso la validacion se realiza fuera del modal."
                                />
                            )}
                            <Descriptions bordered size="small" column={1}>
                                <Descriptions.Item label="Organizacion">{payPhoneContract.organizationName}</Descriptions.Item>
                                <Descriptions.Item label="Plan actual">{payPhoneContract.planName}</Descriptions.Item>
                                <Descriptions.Item label="Contrato">
                                    {payPhoneContract.contractId ? `#${payPhoneContract.contractNumber}` : 'Sin contrato generado'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Token de tarjeta">
                                    {renderCardTokenStatus(activeCardToken?.status)}
                                </Descriptions.Item>
                                <Descriptions.Item label="Tarjeta">
                                    {formatCardTokenLabel(activeCardToken)}
                                </Descriptions.Item>
                                <Descriptions.Item label="Expiracion">
                                    {formatCardExpiry(activeCardToken)}
                                </Descriptions.Item>
                                <Descriptions.Item label="Titular">
                                    {activeCardToken?.cardHolderName || 'No registrado'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Documento">
                                    {activeCardToken?.document || 'No registrado'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Email">
                                    {activeCardToken?.email || 'No registrado'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Telefono">
                                    {activeCardToken?.phone || 'No registrado'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Ultima validacion">
                                    {activeCardToken?.validationPaidAt
                                        ? `${money(Number(activeCardToken.validationAmount || 0), activeCardToken.validationCurrency || 'USD')} - ${formatUtcDate(activeCardToken.validationPaidAt)}`
                                        : 'Sin validacion'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Reverso">
                                    {renderReverseStatus(activeCardToken?.reverseStatus)}
                                </Descriptions.Item>
                            </Descriptions>
                            <Card size="small">
                                <Row gutter={[12, 12]}>
                                    <Col xs={24} md={12}>
                                        <Statistic title="Operacion" value="Validacion de tarjeta" />
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <Statistic
                                            title="Monto de validacion"
                                            value={money(1, payPhoneContract.currency || 'USD')}
                                        />
                                    </Col>
                                </Row>
                            </Card>
                            {cardTokens.length > 0 && (
                                <>
                                    <Divider orientation="left">Historial de tarjetas</Divider>
                                    <Table
                                        size="small"
                                        rowKey={(record, index) => record.id || record.validationClientTransactionId || `${record.provider || 'token'}-${index}`}
                                        dataSource={cardTokens}
                                        pagination={{ pageSize: 5 }}
                                        columns={[
                                            {
                                                title: 'Estado',
                                                dataIndex: 'status',
                                                render: (value: string) => renderCardTokenStatus(value)
                                            },
                                            {
                                                title: 'Tarjeta',
                                                render: (_value: unknown, record: OrganizationCardToken) => formatCardTokenLabel(record)
                                            },
                                            {
                                                title: 'Expiracion',
                                                render: (_value: unknown, record: OrganizationCardToken) => formatCardExpiry(record)
                                            },
                                            {
                                                title: 'Titular',
                                                dataIndex: 'cardHolderName',
                                                render: (value: string) => value || 'No registrado'
                                            },
                                            {
                                                title: 'Validacion',
                                                render: (_value: unknown, record: OrganizationCardToken) => record.validationPaidAt
                                                    ? `${money(Number(record.validationAmount || 0), record.validationCurrency || 'USD')} - ${formatUtcDate(record.validationPaidAt)}`
                                                    : 'Sin validacion'
                                            },
                                            {
                                                title: 'Reverso',
                                                dataIndex: 'reverseStatus',
                                                render: (value: string) => renderReverseStatus(value)
                                            }
                                        ]}
                                    />
                                </>
                            )}
                        </Space>
                    )}
                </Modal>

                <Modal
                    open={isBillingModalOpen && !!selectedContract}
                    title={isFreeBillingPlan ? 'Generar contrato gratis' : 'Registrar factura'}
                    width={1280}
                    className="tbc-admin-modal"
                    footer={null}
                    destroyOnClose
                    onCancel={() => {
                        setIsBillingModalOpen(false);
                        setSelectedContract(null);
                    }}
                >
                    {selectedContract && (
                        <Space direction="vertical" size={18} style={{ width: '100%' }}>
                            <Row gutter={[16, 16]}>
                                <Col xs={24} md={7}>
                                    <Statistic title="Organizacion" value={selectedContract.organizationName} />
                                </Col>
                                <Col xs={24} md={4}>
                                    <Statistic title="Contrato vence" value={formatCalendarDate(selectedContract.endDate)} />
                                </Col>
                                <Col xs={24} md={5}>
                                    <Statistic title={isFreeBillingPlan ? 'Valor del contrato' : 'Total a facturar'} value={money(totalToInvoice, selectedContract.currency || 'USD')} />
                                </Col>
                                <Col xs={24} md={8}>
                                    <Space direction="vertical" size={4}>
                                        <Text type="secondary">Movimiento</Text>
                                        {isPlanChange ? (
                                            <Tag color="blue" icon={<SwapOutlined />}>Cambio de plan</Tag>
                                        ) : (
                                            <Tag color="green">Mismo plan</Tag>
                                        )}
                                    </Space>
                                </Col>
                            </Row>

                            <Row gutter={[16, 16]}>
                                <Col xs={24} md={12}>
                                    <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, padding: 14, minHeight: 112 }}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text type="secondary">Plan actual del contrato</Text>
                                            <Text strong style={{ fontSize: 18 }}>{selectedContract.planName}</Text>
                                            <Text>{money(currentPlanPrice, selectedContract.currency || 'USD')} por periodo</Text>
                                            <Text type="secondary">Contrato #{selectedContract.contractNumber}</Text>
                                        </Space>
                                    </div>
                                </Col>
                                <Col xs={24} md={12}>
                                    <div style={{ border: isPlanChange ? '1px solid #1677ff' : '1px solid #d9d9d9', borderRadius: 6, padding: 14, minHeight: 112, background: isPlanChange ? '#f0f7ff' : '#fff' }}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text type="secondary">{isFreeBillingPlan ? 'Plan nuevo para generar contrato' : 'Plan nuevo a facturar y renovar'}</Text>
                                            <Text strong style={{ fontSize: 18 }}>
                                                {selectedBillingPlan?.name || selectedContract.planName}
                                            </Text>
                                            <Text>{money(newPlanPrice, selectedContract.currency || 'USD')} por periodo</Text>
                                            <Text type="secondary">
                                                {isFreeBillingPlan
                                                    ? 'No se generara factura para este plan.'
                                                    : isPlanChange ? 'La renovacion se creara con este plan.' : 'Se mantiene el plan actual.'}
                                            </Text>
                                        </Space>
                                    </div>
                                </Col>
                            </Row>

                            {isFreeBillingPlan ? (
                                <Alert
                                    type="info"
                                    showIcon
                                    message="Este plan es gratis. Solo se generara un nuevo contrato; no se solicitaran datos tributarios, archivos ni metodos de pago."
                                />
                            ) : (
                                <Alert
                                    type="info"
                                    showIcon
                                    message="Si el cliente paga antes de la fecha de vencimiento, los dias restantes se reconocen como saldo a favor en la siguiente renovacion."
                                />
                            )}

                            <Form
                                form={form}
                                layout="vertical"
                                onValuesChange={(changedValues) => {
                                    if (changedValues.billingPeriods) {
                                        setBillingPeriods(Number(changedValues.billingPeriods || 1));
                                    }
                                    if (changedValues.planId) {
                                        setSelectedPlanId(changedValues.planId);
                                    }
                                    if (changedValues.status) {
                                        handleStatusChange(changedValues.status);
                                    }
                                }}
                            >
                                <Row gutter={[16, 0]}>
                                    {!isFreeBillingPlan && (
                                        <Col xs={24} md={5}>
                                            <Form.Item
                                                label="Numero"
                                                name="number"
                                                rules={[{ required: true, message: 'Ingrese el numero de factura' }]}
                                            >
                                                <Input placeholder="001-001-000000123" />
                                            </Form.Item>
                                        </Col>
                                    )}
                                    <Col xs={24} md={5}>
                                        <Form.Item
                                            label={isFreeBillingPlan ? 'Plan del contrato' : 'Plan a facturar'}
                                            name="planId"
                                            rules={[{ required: true, message: isFreeBillingPlan ? 'Seleccione el plan del contrato' : 'Seleccione el plan a facturar' }]}
                                        >
                                            <Select
                                                showSearch
                                                optionFilterProp="label"
                                                options={plans.map(plan => ({
                                                    value: plan.id || '',
                                                    label: `${plan.name} - ${money(Number(plan.price || 0), selectedContract.currency || 'USD')}`
                                                }))}
                                            />
                                        </Form.Item>
                                    </Col>
                                    {!isFreeBillingPlan && (
                                        <>
                                            <Col xs={24} md={3}>
                                                <Form.Item
                                                    label="Periodos"
                                                    name="billingPeriods"
                                                    rules={[{ required: true, message: 'Ingrese los periodos' }]}
                                                >
                                                    <InputNumber min={1} max={60} style={{ width: '100%' }} />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} md={4}>
                                                <Form.Item label="Estado" name="status">
                                                    <Select
                                                        options={[
                                                            { value: 'PAGADO', label: 'PAGADO' },
                                                            { value: 'PENDIENTE', label: 'PENDIENTE' }
                                                        ]}
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} md={4}>
                                                <Form.Item label="Estado SRI" name="statusSri">
                                                    <Select
                                                        disabled
                                                        options={[
                                                            { value: 'MANUAL', label: 'MANUAL' },
                                                        ]}
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} md={3}>
                                                <Form.Item label="Integracion SRI" name="integrationSri">
                                                    <Select
                                                        disabled
                                                        options={[
                                                            { value: true, label: 'SI' }
                                                        ]}
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </>
                                    )}
                                </Row>

                                {!isFreeBillingPlan && (
                                    <>
                                <Divider orientation="left">Cliente</Divider>

                                <Row gutter={[16, 0]}>
                                    <Col xs={24} md={5}>
                                        <Form.Item
                                            label="RUC"
                                            name="ruc"
                                            rules={[{ required: true, message: 'Ingrese el RUC' }]}
                                        >
                                            <Input
                                                addonAfter={<Button type="link" size="small" icon={<SearchOutlined />} onClick={handleSearchClientData} />}
                                                onPressEnter={handleSearchClientData}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={7}>
                                        <Form.Item
                                            label="Nombre"
                                            name="name"
                                            rules={[{ required: true, message: 'Ingrese el nombre' }]}
                                        >
                                            <Input />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={6}>
                                        <Form.Item
                                            label="Email"
                                            name="email"
                                            rules={[{ required: true, message: 'Ingrese el email' }]}
                                        >
                                            <Input />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={6}>
                                        <Form.Item
                                            label="Telefono"
                                            name="phone"
                                            rules={[{ required: true, message: 'Ingrese el telefono' }]}
                                        >
                                            <Input />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24}>
                                        <Form.Item
                                            label="Direccion"
                                            name="address"
                                            rules={[{ required: true, message: 'Ingrese la direccion' }]}
                                        >
                                            <Input />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Divider orientation="left">Archivos</Divider>

                                <Row gutter={[16, 0]}>
                                    <Col xs={24} md={8}>
                                        <Text strong>Documento</Text>
                                        <Input type="file" onChange={(event) => handleFileChange(event, 'document')} style={{ marginTop: 6 }} />
                                        {documentFile && <Text type="secondary">{documentFile.fileName}</Text>}
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <Form.Item
                                            label="RIDE"
                                            name="rideFile"
                                            rules={[
                                                {
                                                    validator: () => rideFile
                                                        ? Promise.resolve()
                                                        : Promise.reject(new Error('Seleccione el archivo RIDE'))
                                                }
                                            ]}
                                        >
                                            <>
                                                <Input type="file" onChange={(event) => handleFileChange(event, 'ride')} />
                                                {rideFile && <Text type="secondary">{rideFile.fileName}</Text>}
                                            </>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <Form.Item
                                            label="Autorizacion"
                                            name="authorization"
                                            rules={[{ required: true, message: 'Ingrese la autorizacion' }]}
                                        >
                                            <Input />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Divider orientation="left">Metodos de pago</Divider>

                                {status === 'PENDIENTE' ? (
                                    <Alert type="warning" showIcon message="La factura pendiente se registra sin metodos de pago ni paymentDate." />
                                ) : (
                                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                        {paymentRows.map(row => (
                                            <Row gutter={[12, 12]} key={row.key} align="middle">
                                                <Col xs={24} md={10}>
                                                    <Select
                                                        value={row.organizationPaymentMethodId}
                                                        onChange={(value) => updatePaymentRow(row.key, 'organizationPaymentMethodId', value)}
                                                        options={paymentMethods.map(item => ({ value: item.id, label: item.name }))}
                                                        style={{ width: '100%' }}
                                                    />
                                                </Col>
                                                <Col xs={18} md={8}>
                                                    <InputNumber
                                                        min={0}
                                                        precision={2}
                                                        value={row.amount}
                                                        onChange={(value) => updatePaymentRow(row.key, 'amount', value)}
                                                        style={{ width: '100%' }}
                                                    />
                                                </Col>
                                                <Col xs={6} md={6}>
                                                    <Button danger block onClick={() => removePaymentRow(row.key)}>
                                                        Quitar
                                                    </Button>
                                                </Col>
                                            </Row>
                                        ))}
                                        <Button icon={<PlusOutlined />} onClick={addPaymentRow}>
                                            Agregar metodo
                                        </Button>
                                        <Form.Item name="paymentSummary" style={{ marginBottom: 0 }}>
                                            <Alert
                                                type={Math.abs(pendingAmount) <= 0.01 ? 'success' : 'warning'}
                                                showIcon
                                                message={`Pagos: ${money(paymentTotal, selectedContract.currency || 'USD')} | Diferencia: ${money(pendingAmount, selectedContract.currency || 'USD')}`}
                                            />
                                        </Form.Item>
                                    </Space>
                                )}
                                    </>
                                )}

                                <Divider />

                                <Space>
                                    <Button type="primary" icon={<SaveOutlined />} onClick={handleSubmit}>
                                        {isFreeBillingPlan ? 'Generar contrato' : 'Registrar factura'}
                                    </Button>
                                    {!isFreeBillingPlan && (
                                        <Button icon={<ReloadOutlined />} onClick={() => loadSalesDocuments(selectedContract.contractId)}>
                                            Ver facturas
                                        </Button>
                                    )}
                                </Space>
                            </Form>

                            {!isFreeBillingPlan && (
                                <>
                                    <Row gutter={[12, 12]} align="middle">
                                        <Col xs={24} sm={10} md={8} lg={6}>
                                            <Text strong>Ver facturas</Text>
                                            <Select
                                                value={salesDocumentStatusFilter}
                                                onChange={(value) => setSalesDocumentStatusFilter(value)}
                                                options={[
                                                    { value: 'PAGADO', label: 'Pagadas' },
                                                    { value: 'ANULADO', label: 'Anuladas' },
                                                    { value: 'TODOS', label: 'Todas' }
                                                ]}
                                                style={{ width: '100%', marginTop: 6 }}
                                            />
                                        </Col>
                                    </Row>

                                    <Table
                                        rowKey="id"
                                        columns={salesDocumentColumns}
                                        dataSource={filteredSalesDocuments}
                                        pagination={{ pageSize: 5 }}
                                        scroll={{ x: 900 }}
                                    />
                                </>
                            )}
                        </Space>
                    )}
                </Modal>

                <Modal
                    open={!!selectedSalesDocument}
                    title={selectedSalesDocument ? `Detalle factura ${selectedSalesDocument.number}` : 'Detalle factura'}
                    width={900}
                    footer={[
                        <Button key="close" onClick={() => setSelectedSalesDocument(null)}>
                            Cerrar
                        </Button>
                    ]}
                    onCancel={() => setSelectedSalesDocument(null)}
                    destroyOnHidden
                >
                    {selectedSalesDocument && (
                        <Space direction="vertical" size={18} style={{ width: '100%' }}>
                            <Descriptions bordered size="small" column={{ xs: 1, md: 2 }}>
                                <Descriptions.Item label="Numero">{selectedSalesDocument.number}</Descriptions.Item>
                                <Descriptions.Item label="Estado">
                                    <Tag color={getStatusColor(selectedSalesDocument.status)}>{selectedSalesDocument.status}</Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label="Tipo">{selectedSalesDocument.documentType}</Descriptions.Item>
                                <Descriptions.Item label="Moneda">{selectedSalesDocument.currency}</Descriptions.Item>
                                <Descriptions.Item label="Cliente">{selectedSalesDocument.clientName}</Descriptions.Item>
                                <Descriptions.Item label="RUC">{selectedSalesDocument.clientRuc}</Descriptions.Item>
                                <Descriptions.Item label="Fecha de pago">
                                    {selectedSalesDocument.paymentDate ? formatUtcDate(selectedSalesDocument.paymentDate) : 'No registrado'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Creada">
                                    {formatUtcDate(selectedSalesDocument.createdAt)}
                                </Descriptions.Item>
                                <Descriptions.Item label="Estado SRI">{selectedSalesDocument.statusSri}</Descriptions.Item>
                                <Descriptions.Item label="Integracion SRI">
                                    {selectedSalesDocument.integrationSri ? 'SI' : 'NO'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Autorizacion" span={2}>
                                    {selectedSalesDocument.authorization || <Text type="secondary">No registrado</Text>}
                                </Descriptions.Item>
                                <Descriptions.Item label="Documento">
                                    {renderDocumentPreviewButton('Documento', selectedSalesDocument.documentUrl)}
                                </Descriptions.Item>
                                <Descriptions.Item label="RIDE">
                                    {renderDocumentPreviewButton('RIDE', selectedSalesDocument.ride)}
                                </Descriptions.Item>
                                <Descriptions.Item label="Subtotal">
                                    {money(selectedSalesDocument.subtotal, selectedSalesDocument.currency)}
                                </Descriptions.Item>
                                <Descriptions.Item label="IVA">
                                    {money(selectedSalesDocument.iva, selectedSalesDocument.currency)}
                                </Descriptions.Item>
                                <Descriptions.Item label="Total" span={2}>
                                    <Text strong>{money(selectedSalesDocument.total, selectedSalesDocument.currency)}</Text>
                                </Descriptions.Item>
                            </Descriptions>

                            <div>
                                <Text strong>Metodos de pago</Text>
                                <Table
                                    rowKey={(row) => row.organizationPaymentMethodId}
                                    columns={[
                                        {
                                            title: 'Metodo',
                                            dataIndex: 'name',
                                            key: 'name'
                                        },
                                        {
                                            title: 'Valor',
                                            dataIndex: 'amount',
                                            key: 'amount',
                                            align: 'right' as const,
                                            render: (value: number) => money(value, selectedSalesDocument.currency)
                                        }
                                    ]}
                                    dataSource={selectedSalesDocument.paymentMethods || []}
                                    pagination={false}
                                    size="small"
                                    style={{ marginTop: 8 }}
                                />
                            </div>
                        </Space>
                    )}
                </Modal>

                <Modal
                    open={!!legalDocumentsOrganization}
                    title={legalDocumentsOrganization ? `Documentos legales - ${legalDocumentsOrganization.organizationName}` : 'Documentos legales'}
                    width={980}
                    footer={[
                        <Button key="close" onClick={() => setLegalDocumentsOrganization(null)}>
                            Cerrar
                        </Button>
                    ]}
                    onCancel={() => setLegalDocumentsOrganization(null)}
                    destroyOnHidden
                >
                    <Space direction="vertical" size={14} style={{ width: '100%' }}>
                        <Row gutter={[12, 12]}>
                            <Col xs={24} md={12}>
                                <Alert type={pendingLegalDocuments.length > 0 ? 'warning' : 'success'} showIcon message={`${pendingLegalDocuments.length} documento(s) pendiente(s)`} />
                            </Col>
                            <Col xs={24} md={12}>
                                <Alert type="info" showIcon message={`${currentAcceptedLegalDocuments.length} documento(s) vigente(s) aceptado(s)`} />
                            </Col>
                        </Row>
                        <Tabs
                            items={[
                                {
                                    key: 'pending',
                                    label: `Pendientes (${pendingLegalDocuments.length})`,
                                    children: (
                                        <Table
                                            rowKey={(row) => row.id || `${row.code}-${row.version}`}
                                            dataSource={pendingLegalDocuments}
                                            pagination={{ pageSize: 6 }}
                                            scroll={{ x: 780 }}
                                            locale={{ emptyText: 'La organizacion no tiene documentos legales vigentes pendientes.' }}
                                            columns={[
                                                {
                                                    title: 'Documento',
                                                    dataIndex: 'name',
                                                    key: 'name',
                                                    render: (value: string, row: LegalDocument) => (
                                                        <Space direction="vertical" size={0}>
                                                            <Text strong>{value}</Text>
                                                            <Text type="secondary">{row.code} - version {row.version}</Text>
                                                            {row.description && <Text type="secondary">{row.description}</Text>}
                                                        </Space>
                                                    )
                                                },
                                                {
                                                    title: 'Tipo',
                                                    dataIndex: 'optional',
                                                    key: 'optional',
                                                    render: (value: boolean) => value ? 'Opcional' : 'Obligatorio'
                                                },
                                                {
                                                    title: 'Estado',
                                                    dataIndex: 'status',
                                                    key: 'status',
                                                    render: (value: string) => <Tag color={getStatusColor(value)}>{value}</Tag>
                                                },
                                                {
                                                    title: 'Situacion',
                                                    key: 'situation',
                                                    render: () => <Tag color="warning">Pendiente</Tag>
                                                },
                                                {
                                                    title: '',
                                                    key: 'actions',
                                                    align: 'right' as const,
                                                    render: (_: unknown, row: LegalDocument) => (
                                                        <Button icon={<EyeOutlined />} href={row.url} target="_blank" rel="noreferrer" disabled={!row.url}>
                                                            Ver URL
                                                        </Button>
                                                    )
                                                }
                                            ]}
                                        />
                                    )
                                },
                                {
                                    key: 'accepted',
                                    label: `Aceptados (${currentAcceptedLegalDocuments.length})`,
                                    children: (
                                        <Table
                                            rowKey="id"
                                            dataSource={currentAcceptedLegalDocuments}
                                            pagination={{ pageSize: 6 }}
                                            scroll={{ x: 780 }}
                                            locale={{ emptyText: 'La organizacion no tiene documentos legales vigentes aceptados.' }}
                                            columns={[
                                                {
                                                    title: 'Documento',
                                                    dataIndex: 'name',
                                                    key: 'name',
                                                    render: (value: string, row: OrganizationLegalDocument) => (
                                                        <Space direction="vertical" size={0}>
                                                            <Text strong>{value}</Text>
                                                            <Text type="secondary">{row.code} - version {row.version}</Text>
                                                            {row.description && <Text type="secondary">{row.description}</Text>}
                                                        </Space>
                                                    )
                                                },
                                                {
                                                    title: 'Aceptado',
                                                    dataIndex: 'acceptedAt',
                                                    key: 'acceptedAt',
                                                    render: (value: string) => formatUtcDate(value)
                                                },
                                                {
                                                    title: 'Tipo',
                                                    dataIndex: 'optional',
                                                    key: 'optional',
                                                    render: (value: boolean) => value ? 'Opcional' : 'Obligatorio'
                                                },
                                                {
                                                    title: 'Estado',
                                                    dataIndex: 'status',
                                                    key: 'status',
                                                    render: (value: string) => <Tag color={getStatusColor(value)}>{value}</Tag>
                                                },
                                                {
                                                    title: 'Situacion',
                                                    key: 'situation',
                                                    render: () => <Tag color="success">Aceptado</Tag>
                                                },
                                                {
                                                    title: '',
                                                    key: 'actions',
                                                    align: 'right' as const,
                                                    render: (_: unknown, row: OrganizationLegalDocument) => (
                                                        <Button icon={<EyeOutlined />} onClick={() => setDocumentPreview({
                                                            title: `${row.name} ${row.version}`,
                                                            contentText: row.contentText || '',
                                                            contentType: row.contentType || 'TEXT',
                                                            url: row.url
                                                        })}>
                                                            Visualizar
                                                        </Button>
                                                    )
                                                }
                                            ]}
                                        />
                                    )
                                }
                            ]}
                        />
                    </Space>
                </Modal>

                <Drawer
                    title={documentPreview?.title || 'Documento'}
                    open={!!documentPreview}
                    placement="bottom"
                    height="80vh"
                    onClose={() => setDocumentPreview(null)}
                    destroyOnHidden
                    extra={documentPreview ? (
                        <Button href={documentPreview.url} target="_blank" rel="noreferrer" disabled={!documentPreview.url}>
                            Abrir en otra pestana
                        </Button>
                    ) : null}
                    styles={{
                        body: { padding: 0, background: documentPreview?.contentText ? '#ffffff' : '#111827' }
                    }}
                >
                    {documentPreview?.contentText && documentPreview.contentType === 'HTML' && (
                        <iframe
                            title={documentPreview.title}
                            srcDoc={documentPreview.contentText}
                            style={{
                                width: '100%',
                                height: '100%',
                                border: 0,
                                background: '#ffffff'
                            }}
                        />
                    )}
                    {documentPreview?.contentText && documentPreview.contentType !== 'HTML' && (
                        <pre style={{ margin: 0, minHeight: '100%', padding: 24, whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 14, lineHeight: 1.6 }}>
                            {documentPreview.contentText}
                        </pre>
                    )}
                    {!documentPreview?.contentText && documentPreview?.url && (
                        <iframe
                            title={documentPreview.title}
                            src={documentPreview.url}
                            style={{
                                width: '100%',
                                height: '100%',
                                border: 0,
                                background: '#ffffff'
                            }}
                        />
                    )}
                </Drawer>
                                </>
                            )
                        },
                        {
                            key: 'pending-invoices',
                            label: 'Facturas por emitir',
                            children: (
                                <PlanBillingPendingInvoices
                                    getPendingPaymentRequestInvoices={props.getPendingPaymentRequestInvoices}
                                    postPaymentRequestSalesDocument={props.postPaymentRequestSalesDocument}
                                    reversePaymentRequestPayPhone={props.reversePaymentRequestPayPhone}
                                />
                            )
                        },
                        {
                            key: 'billing-configuration',
                            label: 'Configuración',
                            children: <BillingConfiguration
                                getProfile={props.getProfile}
                                getOrganizationManagementCenters={props.getOrganizationManagementCenters} />
                        },
                        {
                            key: 'direct-sales-document',
                            label: 'Factura directa',
                            children: <DirectSalesDocumentManagement getProfile={props.getProfile} />
                        },
                        {
                            key: 'generated-sales-documents',
                            label: 'Facturas generadas',
                            children: <SalesDocumentsManagement getProfile={props.getProfile} />
                        },
                        {
                            key: 'pending-card-validation',
                            label: 'Validaciones pendientes',
                            children: (
                                <PlanBillingPendingCardValidation
                                    plans={paidPlans}
                                    getPendingCardValidationRequests={props.getPendingCardValidationRequests}
                                    regeneratePaymentRequestPayPhone={props.regeneratePaymentRequestPayPhone}
                                />
                            )
                        },
                        {
                            key: 'metrics',
                            label: 'Metricas',
                            children: (
                                <PlanBillingMetrics
                                    getSalesDocuments={props.getSalesDocuments}
                                    getPendingPaymentRequestInvoices={props.getPendingPaymentRequestInvoices}
                                />
                            )
                        },
                        {
                            key: 'expenses',
                            label: 'Gastos',
                            children: (
                                <PlanBillingExpenses
                                    getExpenseCategories={props.getExpenseCategories}
                                    postExpenseCategory={props.postExpenseCategory}
                                    putExpenseCategory={props.putExpenseCategory}
                                    getExpenses={props.getExpenses}
                                    postExpense={props.postExpense}
                                    putExpense={props.putExpense}
                                    cancelExpense={props.cancelExpense}
                                    getExpenseDocument={props.getExpenseDocument}
                                    getFinancialMetrics={props.getFinancialMetrics}
                                    getExpenseOptions={props.getExpenseOptions}
                                    getCompanyUsers={props.getCompanyUsers}
                                    searchOrganizations={props.searchOrganizations}
                                />
                            )
                        },
                        {
                            key: 'people',
                            label: 'Personas',
                            children: (
                                <PlanBillingPeople
                                    getCompanyUsers={props.getCompanyUsers}
                                    postCompanyUser={props.postCompanyUser}
                                    putCompanyUser={props.putCompanyUser}
                                />
                            )
                        }
                    ].filter(item => {
                        const key = String(item.key);
                        const billingKeys = ['billing-configuration', 'direct-sales-document', 'generated-sales-documents'];
                        const companyKeys = ['metrics', 'expenses', 'people'];

                        if (moduleView === 'billing') return billingKeys.includes(key);
                        if (moduleView === 'company') return companyKeys.includes(key);
                        return !billingKeys.includes(key) && !companyKeys.includes(key);
                    })}
                />
            </Space>
        </div>
    );
};

const mapStateToProps = (_state: AppState) => ({
});

export default connect(mapStateToProps, {
    getContractsExpiring,
    getContractSalesDocuments,
    getSalesDocuments,
    getOrganizationLegalDocuments,
    getLegalDocuments,
    postContractSalesDocument,
    postContract,
    getOrganizationClientData,
    preparePublicPayPhoneTrial,
    cancelSalesDocument,
    getExpenseCategories,
    postExpenseCategory,
    putExpenseCategory,
    getExpenses,
    postExpense,
    putExpense,
    cancelExpense,
    getExpenseDocument,
    getFinancialMetrics,
    getExpenseOptions,
    getCompanyUsers,
    postCompanyUser,
    putCompanyUser,
    getPendingPaymentRequestInvoices,
    getOrganizationPaymentRequests,
    getPendingCardValidationRequests,
    regeneratePaymentRequestPayPhone,
    postPaymentRequestSalesDocument,
    reversePaymentRequestPayPhone,
    cancelContract,
    getOrganizationPaymentMethod,
    getClientData,
    getPlans,
    searchOrganizations,
    getActiveSpeciality,
    getMedicalRecordSections,
    postOnboardingRegister,
    getProfile,
    getOrganizationManagementCenters,
    getOrganizationCardTokens,
    getActiveOrganizationCardToken,
    prepareOrganizationCardTokenPayPhone,
    chargeContractWithCardToken,
    disableOrganizationCardToken
})(PlanBilling);
